import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Message, CallTranscript, MessageItem } from '../types';
import MessageCard from './MessageCard';
import CreditMemoEditor from './CreditMemoEditor';
import ChannelBadge from './ChannelBadge';
import MessageComments from './MessageComments';
import CustomerReply from './CustomerReply';
import OrderItemEditor from './OrderItemEditor';
import OrderDiffPanel, { DiffLine } from './OrderDiffPanel';
import CustomerPanel from './CustomerPanel';
import SuggestedReply from './SuggestedReply';
import OpenQuestion from './OpenQuestion';
import CustomerConfirmSelect from './CustomerConfirmSelect';
import EditableOrderSelect from './EditableOrderSelect';
import PreviewTabs from './PreviewTabs';
import ThreadModal from './ThreadModal';
import { getMessageItems, getThreadOrderItems, getThreadRmaItems, moveRmaLine, createSalesOrder, createRMA, setNeedsReview, getEditableOrders, getSalesOrderLines, getSalesOrderHeader, SoHeaderSeed, submitOrderChanges, getPendingOrderEdits, resolvePendingOrderEdit, saveManualOrderEdit, cancelSalesOrder, setThreadViewing, setMessageTargetCustomer, advanceThread, reassignMessageThread, splitMessageToNewThread, getCallTranscript, setThreadEditTarget, getThreadEditTarget, ensureSallyHelpNote as apiEnsureSallyHelpNote, getThreadIsCredit, getThreadCmItems, createCreditMemo, CmHeaderFields, getCurrentUser, listPreviewGroups, createPreviewGroup, deletePreviewGroup, setPreviewCustomer, PreviewType, getOrderPromoLines, PromoLine, showToast } from '../services/api';

interface ConversationCardProps {
  messages: Message[];
  onStatusChange: (id: number, status: string) => void;
  onAfterSend: () => void;
  // The single open thread's root id (lifted to MessageFeed). A thread is open
  // when its key === openThreadId; its detail body renders in a modal overlay.
  openThreadId: number | null;
  // Set/clear the open thread. Clicking a collapsed sub-thread card opens it;
  // passing null closes the modal.
  onOpenThread: (threadId: number | null) => void;
  // Managers/admin History tab: when true, COMPLETED sub-threads are shown instead of hidden.
  showCompleted?: boolean;
}

// When a rep OPENS a "Sally needs help" thread, ask the backend to post Sally's
// DETAILED, grounded explanation of what she's stuck on into the thread's Comments
// (its running log) instead of auto-opening her chat. The backend composes it with
// one grounded model call and is idempotent (no model call / no duplicate when the
// thread isn't stuck or she's already commented since the last message). Once it
// resolves, nudge the Comments panel to reload so the note appears without a
// reopen. Fully NON-FATAL — a failure here must never interfere with opening.
const ensureSallyHelpNote = async (threadRootId: number): Promise<void> => {
  try {
    const res = await apiEnsureSallyHelpNote(threadRootId);
    if (res && res.posted) {
      window.dispatchEvent(new CustomEvent('sl-comments-refresh', {
        detail: { messageId: threadRootId },
      }));
    }
  } catch (err) {
    console.error('ensureSallyHelpNote failed (non-fatal):', err);
  }
};

const intentBadgeStyles: Record<string, string> = {
  'ORDER': 'bg-green-100 text-green-800',
  'QUESTION': 'bg-blue-100 text-blue-800',
  'INFO': 'bg-purple-100 text-purple-800',
  'COMPLAINT': 'bg-red-100 text-red-800',
  // RMA (returns) — orange, matching the RMA preview theme; distinct from gray
  // (UNKNOWN) and the amber "Needs Review" pill.
  'RMA': 'bg-orange-100 text-orange-800',
  'UNKNOWN': 'bg-gray-100 text-gray-800',
};

const fmtTime = (s: string): string => {
  try {
    const d = new Date(s);
    const h = d.getHours();
    const hr = h % 12 || 12;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${d.getMonth() + 1}/${d.getDate()} ${hr}:${d.getMinutes().toString().padStart(2, '0')} ${ampm}`;
  } catch (e) {
    return '';
  }
};

// Effective chronological position of a message for transcript ordering. Uses
// received_at (inbound arrival / outbound send time — both are stamped on the
// row), but a missing/unparseable value would make `new Date(...)` collapse to
// epoch 0 (or NaN) and yank that bubble to the TOP of the thread — the classic
// "people talking over each other" symptom. So fall back to created_at, and let
// the id tie-breaker settle anything still equal (ids are assigned in write
// order). Never returns NaN.
const msgTime = (m: Message): number => {
  const t = new Date(m.received_at as any).getTime();
  if (!Number.isNaN(t) && t > 0) return t;
  const c = new Date((m as any).created_at).getTime();
  return Number.isNaN(c) ? 0 : c;
};

// Split an ASC-ordered message list into speaker "turns": maximal runs of
// consecutive messages from the SAME speaker (same direction, and — for
// outbound — the same sender, so two different reps don't merge). Rendering one
// header per turn instead of per bubble makes a long back-and-forth read as
// clean turn-taking rather than a wall of repeated name/time labels.
const speakerKey = (m: Message): string =>
  m.direction === 'outbound' ? `out:${m.sent_by || ''}` : `in:${m.sender_id || ''}`;
const buildTurns = (msgs: Message[]): Message[][] => {
  const turns: Message[][] = [];
  for (const m of msgs) {
    const last = turns[turns.length - 1];
    if (last && speakerKey(last[0]) === speakerKey(m)) last.push(m);
    else turns.push([m]);
  }
  return turns;
};

// Is this CALL row a VOICEMAIL (a missed call nobody answered)? The publish
// side encodes it in the external_id/sender_id PREFIX — 'vm:{ct_id}' for a
// voicemail vs 'call:{ct_id}' for a recorded conversation — so the feed card
// can say so without opening the thread and without a new messages column.
const isVoicemailMessage = (m: { external_id?: string; sender_id?: string } | null | undefined): boolean =>
  /^vm:/.test((m?.external_id || m?.sender_id || ''));

// Call-thread (channel='CALL') header helpers: duration m:ss + a readable
// phone number for the transcript-detail metadata line.
const fmtCallDuration = (secs: number | null | undefined): string => {
  if (secs == null || secs < 0) return '';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const fmtCallNumber = (num: string | null | undefined): string => {
  if (!num) return '';
  const digits = num.replace(/\D/g, '');
  const d10 = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d10.length === 10) return `(${d10.slice(0, 3)}) ${d10.slice(3, 6)}-${d10.slice(6)}`;
  return num;
};

interface SubThreadProps {
  messages: Message[];
  onStatusChange: (id: number, status: string) => void;
  onAfterSend: () => void;
  // isOpen === true means THIS sub-thread is the one open in the modal overlay.
  // onToggle opens it (when collapsed) or closes the modal (when open).
  isOpen: boolean;
  onToggle: () => void;
  // Which view the OPEN modal shows (DETAILS vs the thread's COMMENT history).
  // LIFTED to ConversationCard rather than kept in local state: on an employee
  // conversation, opening a sub-thread MOVES it from its customer group into the
  // stable slot (a different tree position), which REMOUNTS this component and
  // would reset a local useState back to its 'details' default — so clicking
  // Comments from the collapsed card opened to Details. Holding it in the parent
  // (which doesn't remount) makes the intended view survive that remount.
  modalView: 'comments' | 'details';
  onSetModalView: (view: 'comments' | 'details') => void;
  // Other sub-threads in this conversation a mis-placed message can be moved to,
  // and the handler that performs the move (human override of AI thread placement).
  otherThreads?: { key: number; label: string }[];
  onReassign?: (messageId: number, threadId: number) => void;
  // Split this message OUT into its own brand-new thread (human override when the
  // AI placed it in a thread it doesn't belong to and there's no right one yet).
  onSplitToNewThread?: (messageId: number) => void;
}

// A subtle icon control beside an inbound message: move it to a different thread
// of this same conversation when the AI placed it wrong. The dropdown lists the
// available THREAD NUMBERS (e.g. "#3051 · Bryans Stumps").
const MoveMessageControl: React.FC<{
  messageId: number;
  otherThreads: { key: number; label: string }[];
  onReassign: (messageId: number, threadId: number) => void;
  onSplitToNewThread?: (messageId: number) => void;
}> = ({ messageId, otherThreads, onReassign, onSplitToNewThread }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [num, setNum] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const pick = async (threadId: number) => {
    setBusy(true);
    try { await onReassign(messageId, threadId); } finally { setBusy(false); setOpen(false); }
  };
  const splitOut = async () => {
    if (!onSplitToNewThread) return;
    setSplitting(true);
    try { await onSplitToNewThread(messageId); } finally { setSplitting(false); }
  };
  // Move to ANY thread by number (the backend accepts any existing thread id and
  // 404s an invalid one). This is the escape hatch when auto-routing put a message
  // in the wrong place and the right thread isn't one of the in-view siblings.
  const submitNum = async () => {
    const id = parseInt(num.trim().replace(/^#/, ''), 10);
    if (!Number.isFinite(id) || id <= 0) return;
    await pick(id);
    setNum('');
  };
  return (
    <div className="relative flex-shrink-0 flex items-center gap-0.5" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        title="Move this message to another thread"
        aria-label="Move this message to another thread"
        className={`p-1.5 rounded-full transition-colors disabled:opacity-50 ${open ? 'text-brand-700 bg-brand-50' : 'text-gray-300 hover:text-brand-700 hover:bg-gray-100'}`}
      >
        {busy ? (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4" />
          </svg>
        )}
      </button>
      {/* Sibling control: split this message OUT into its own NEW thread. */}
      {onSplitToNewThread && (
        <button
          onClick={splitOut}
          disabled={splitting}
          title="Create a new thread with this message"
          aria-label="Create a new thread with this message"
          className="p-1.5 rounded-full transition-colors disabled:opacity-50 text-gray-300 hover:text-brand-700 hover:bg-gray-100"
        >
          {splitting ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          )}
        </button>
      )}
      {open && (
        <div className="absolute z-30 right-0 left-auto mt-1 w-60 max-w-[calc(100vw-2rem)] max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-xl py-1">
          <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Move to thread #</div>
          {/* Move to ANY thread by number (not just in-view siblings). */}
          <div className="px-3 py-1.5 flex items-center gap-1">
            <span className="text-sm text-gray-400">#</span>
            <input
              type="number"
              inputMode="numeric"
              value={num}
              onChange={(e) => setNum(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitNum(); }}
              placeholder="thread number"
              className="w-32 px-1.5 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-brand-400"
            />
            <button
              onClick={submitNum}
              disabled={busy || !num.trim()}
              className="px-2 py-1 text-xs font-medium text-white bg-brand-600 rounded hover:bg-brand-700 disabled:opacity-40"
            >
              Move
            </button>
          </div>
          {otherThreads.length > 0 && (
            <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wide border-t border-gray-100 mt-1">Or a thread here</div>
          )}
          {otherThreads.map(t => (
            <button
              key={t.key}
              onClick={() => pick(t.key)}
              className="block w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-brand-50"
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Order-compare normalizers (OrderDiffPanel) ──────────────────────────────
// Reduce each side of the edit-mode compare to the panel's DiffLine shape.
// NetSuite lines (getSalesOrderLines / the overlaid editLines): {line, item_id,
// item_name, quantity, rate}. The editor's live EditableItem[]: id (= the NS line
// number for loaded lines, negative for rep-added rows), item_id, quantity (base),
// unit_price, plus DISCOUNT/MARKETING credit rows (rate = -credit_amount).
//
// The compare shows the WHOLE existing order — product lines AND discount/marketing
// credit lines — so a rep sees everything on the SO (and future discount edits diff
// here too). The alignment key is the NetSuite LINE NUMBER, not item_id: an order can
// carry several credit lines under ONE shared item_id (e.g. SO120109's three DIS007
// lines on item 12855), which item_id-keying would collapse into phantom changes.
// Every existing line on both sides carries its NS line number, so line-keyed
// alignment lines each row up with its own baseline. Credit lines have NO quantity in
// NetSuite (null -> 0); the editor defaults their qty to 1 for its own display, so we
// normalize credit qty back to 0 here to match the NS baseline (else a phantom "Qty"
// change). Rate is the negative credit amount, matching the SO's negative line rate.
const _isCreditKind = (k?: string | null) => k === 'DISCOUNT' || k === 'MARKETING';

const nsLinesToDiff = (lines: any[] | null | undefined): DiffLine[] =>
  (lines || []).map((l) => ({
    line: l.line != null ? Number(l.line) : null,
    item_id: l.item_id != null ? Number(l.item_id) : null,
    name: l.item_name || l.resolved_item || l.shorthand || '',
    qty: Number(l.quantity) || 0,
    rate: l.rate != null ? Number(l.rate) : null,
  }));

const editableItemsToDiff = (items: any[] | null | undefined): DiffLine[] =>
  (items || [])
    .filter((i) => !i.removed && i.item_id != null)
    .map((i) => {
      const isCredit = _isCreditKind(i.line_kind);
      return {
        // The editor's row id IS the NS line number for loaded lines (positive);
        // rep-added rows use a negative placeholder id -> no NS line yet (null).
        line: i.id != null && Number(i.id) > 0 ? Number(i.id) : null,
        item_id: Number(i.item_id),
        name: i.resolved_item || i.item_name || i.shorthand || '',
        qty: isCredit ? 0 : (Number(i.quantity) || 0),
        rate: isCredit
          ? -(Number(i.credit_amount) || 0)
          : (i.unit_price != null ? Number(i.unit_price) : null),
      };
    });

// One sub-thread. The subject row carries the thread type tag, the progress
// (status) tracker, and the Show Details / Comments actions. Reply lives at the
// bottom of the expanded message list (like a text thread). Open/closed state
// is owned by the parent so only one sub-thread is expanded at a time.
const SubThread: React.FC<SubThreadProps> = ({ messages, onStatusChange, onAfterSend, isOpen, onToggle, modalView, onSetModalView, otherThreads = [], onReassign, onSplitToNewThread }) => {
  // Root card element + the screen rect captured when this thread is opened, so
  // the modal can EXPAND OUT of the card (grow from here) instead of popping in.
  const rootRef = useRef<HTMLDivElement>(null);
  const [originRect, setOriginRect] = useState<DOMRect | null>(null);
  // Hot Load pre-check (#8): if any message in this thread was flagged hot_load
  // by the backend, seed the SO preview's Hot Load checkbox. The rep can still
  // un-check it (OrderItemEditor initializes once from this, no clobber).
  const threadHotLoad = messages.some(m => !!m.hot_load);
  // Which view the OPEN modal shows (DETAILS vs the thread's COMMENT history) is
  // owned by the parent (modalView / onSetModalView props) so it survives the
  // remount that opening triggers on an employee conversation. See SubThreadProps.
  // Open the thread to a specific view (or, when already open, just switch the
  // view). Snapshots the card position for the modal's expand animation BEFORE any
  // layout shift, and — for a Sally-needs-help thread — has her post a detailed
  // explanation to the thread's Comments (see ensureSallyHelpNote) not open her chat.
  const openTo = (view: 'comments' | 'details') => {
    const opening = !isOpen;
    onSetModalView(view);
    // Switching TO details: nudge a re-measure so auto-sizing elements (the draft
    // preview textarea) that were hidden on the Comments view size correctly once
    // shown. Deferred a tick so the DOM has un-hidden first.
    if (view === 'details') {
      setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
    }
    if (opening) {
      if (rootRef.current) setOriginRect(rootRef.current.getBoundingClientRect());
      // When OPENING a thread Sally flagged for help, have her post a detailed,
      // grounded explanation of what she's stuck on to the thread's Comments
      // (idempotently, server-side) rather than auto-opening her chat — reps found
      // the chat popping up on every open annoying. Her note lands in the Comments
      // panel (which reads subject.id); the rep can still open the chat manually via
      // the "Ask Sally" launcher when THEY want it. Fire-and-forget.
      if (subject.sally_needs_help) {
        void ensureSallyHelpNote(subject.id);
      }
      onToggle();
    }
  };
  // The card's subject-row click opens the modal to its default (details) view.
  const openFromCard = () => openTo('details');
  const [showReply, setShowReply] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);
  const [advanceBusy, setAdvanceBusy] = useState(false);
  // Bumped after a successful "Prompt AI" so the SuggestedReply drafts list
  // refetches and the newly-generated message appears WITHOUT the human having to
  // close and reopen the sub-thread (a draft insert doesn't change any message's
  // updated_at, so threadRefreshKey alone wouldn't trigger the refetch).
  const [advanceNonce, setAdvanceNonce] = useState(0);
  // AUTO-REFRESH while the sub-thread is OPEN (Michael 2026-06-29): a backend
  // change (an item reconciled onto the order, a server-generated draft, a
  // customer reconciliation) has no advanceNonce to bump and may not change the
  // bound message's updated_at, so the rep had to close/reopen or hard-refresh to
  // see it. A light periodic tick (every 5s, only while THIS sub-thread is open)
  // is composed into the children's refreshKeys so they refetch their own cheap
  // single-thread endpoints on their own cadence. OrderItemEditor's dirtyRef guard
  // still prevents clobbering unsaved rep edits; the feed identity-skip is untouched.
  // 2s cadence (Michael 2026-06-30): the Prompt-AI button now grounds the preview
  // DETERMINISTICALLY and UP FRONT, so its writes commit early in the request — a
  // tight poll surfaces the corrected items within ~2s, before the brain even
  // finishes the draft. The poll target is the cheap ~0.85s whole-thread sweep.
  const [openTick, setOpenTick] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    // Poll cadence (Michael 2026-08-03): eased 2s -> 5s and PAUSED while the
    // browser tab is hidden. Each poll fans out into several whole-thread sweeps
    // (detection + the mounted editors); at 2s on every open card that was a
    // steady drum of heavy JOINs that (with the formerly-uncached NetSuite item
    // lookups) starved the shared pool and made Sally slow app-wide. The
    // per-item NetSuite lookups are now cached (message_processor _attr_cache_*),
    // so 5s still surfaces a Prompt-Sally result well within its own multi-second
    // run while cutting steady-state load ~2.5x. A hidden tab does no work at all.
    let id: any = null;
    const start = () => {
      if (id != null) return;
      id = setInterval(() => setOpenTick(t => t + 1), 5000);
    };
    const stop = () => { if (id != null) { clearInterval(id); id = null; } };
    const onVis = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        stop();
      } else {
        setOpenTick(t => t + 1);  // refresh immediately on refocus
        start();
      }
    };
    if (typeof document === 'undefined' || !document.hidden) start();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }
    return () => {
      stop();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
    };
  }, [isOpen]);
  // Multi-preview DETECTION guards (see the detection effect below). openTick re-runs
  // that effect every 2s, but its batched thread sweep can take LONGER than 2s
  // (get_thread_order_items is a heavy JOIN). A per-run "cancelled" flag would then
  // discard every response before it landed, so RMA/CM previews (which mount ONLY
  // from that effect) would never appear on slower threads. These refs let a response
  // apply as long as we're still mounted and on the SAME thread — only a real thread
  // switch invalidates an in-flight response.
  const detectionRootRef = useRef<number | null>(null);
  const detectionMountedRef = useRef(true);
  useEffect(() => () => { detectionMountedRef.current = false; }, []);
  // Optimistic-mutation guard for the multi-preview detection poll. A LOCAL
  // preview mutation (add/remove a tab, re-point a preview's customer, move an
  // RMA line) bumps mutationSeqRef immediately AND schedules a fresh poll via
  // detectNonce. The detection effect snapshots the seq before its fetch fires
  // and DISCARDS any response whose seq is stale — i.e. a sweep that was already
  // in flight when the mutation happened, computed against pre-mutation server
  // state. Without this, that late sweep re-applies the OLD tabs/customer over
  // the optimistic change, and the next poll flips it back: the exact
  // "change appears then immediately disappears / flickers" the reps hit while
  // working in the modal. The mutation handlers await their server WRITE before
  // the detectNonce poll fires, so that authoritative poll reads committed state
  // — instant apply, no 2s stale window, no clobber. Mirrors OrderItemEditor's
  // dirtyRef guard, but per-mutation (sequence) rather than a single boolean.
  const mutationSeqRef = useRef(0);
  const [detectNonce, setDetectNonce] = useState(0);
  // Invalidate any in-flight sweep NOW (call at the START of a mutation, before
  // its await) so a response that predates the mutation can't land.
  const invalidateDetect = useCallback(() => { mutationSeqRef.current += 1; }, []);
  // Fire a fresh authoritative sweep (call AFTER the mutation's write + optimistic
  // local update). Bumps the seq AGAIN first, so any sweep that fired DURING the
  // await (an openTick / threadRefreshKey re-run that may have read pre-commit
  // state) is also discarded — only this post-commit sweep survives to reconcile.
  const refreshDetect = useCallback(() => {
    mutationSeqRef.current += 1;
    setDetectNonce(n => n + 1);
  }, []);
  // Force-correct repaint (R2.6 #8): when the AI Assist chat force-corrects this
  // thread's staged order side-by-side, it dispatches a decoupled 'sl-force-correct'
  // window event (same pattern as sl-open-assist / sl-focus-convo). Bumping this
  // nonce composes into the OrderItemEditor refreshKey below, so the SO-preview
  // list re-fetches (via the EXISTING getThreadOrderItems path) IMMEDIATELY rather
  // than waiting on the 2s openTick poll. Mirrors advanceNonce's on-action bump.
  // The 2s poll would also pick up the change (the force-correct bumps the row's
  // updated_at), so this is a responsiveness upgrade, not the only path. The
  // listener is registered whenever this sub-thread is open, and filters on the
  // event's threadRef so only the affected sub-thread repaints.
  const [forceCorrectNonce, setForceCorrectNonce] = useState(0);
  // "Sally is working" shimmer: driven by a decoupled 'sl-sally-working' window
  // event (fired by the AI Assist chat while thinking, and around Prompt-AI /
  // advance) filtered to THIS thread, OR by the backend ai_working flag (a live
  // status='PROCESSING' ingest). Mirrors the sl-force-correct pattern.
  const [sallyWorkingEvt, setSallyWorkingEvt] = useState(false);
  // SO create flow — PER PREVIEW GROUP (a thread can carry multiple order previews,
  // one per tab), so status/result are keyed by group number, mirroring RMA.
  const [soStatusByGroup, setSoStatusByGroup] = useState<Record<number, 'idle' | 'loading' | 'success' | 'error'>>({});
  const [soResultByGroup, setSoResultByGroup] = useState<Record<number, string>>({});
  // RMA (returns) create flow — PER RMA GROUP (a thread can require multiple
  // Return Authorizations), so status/result are keyed by group number. Separate
  // from the SO create feedback.
  const [rmaStatusByGroup, setRmaStatusByGroup] = useState<Record<number, 'idle' | 'loading' | 'success' | 'error'>>({});
  const [rmaResultByGroup, setRmaResultByGroup] = useState<Record<number, string>>({});
  // Credit Memo (CM) create flow — PER PREVIEW GROUP too (multiple credit memos per
  // thread), keyed by group number.
  const [cmStatusByGroup, setCmStatusByGroup] = useState<Record<number, 'idle' | 'loading' | 'success' | 'error'>>({});
  const [cmResultByGroup, setCmResultByGroup] = useState<Record<number, string>>({});
  // ── Multi-preview tabs ──────────────────────────────────────────────────────
  // The distinct groups (tabs) present on the thread for each preview TYPE, one
  // editor per group. RMA already worked this way; SO/CM now match. NULL group => 1.
  const [soGroups, setSoGroups] = useState<number[]>([]);
  const [rmaGroups, setRmaGroups] = useState<number[]>([]);
  const [cmGroups, setCmGroups] = useState<number[]>([]);
  // Which tab is currently shown per type (show-one-at-a-time).
  const [activeSoGroup, setActiveSoGroup] = useState<number>(1);
  const [activeRmaGroup, setActiveRmaGroup] = useState<number>(1);
  const [activeCmGroup, setActiveCmGroup] = useState<number>(1);
  // Per-preview CUSTOMER override, keyed `${type}:${group}` (e.g. "SO:2"). Seeded
  // from the preview_groups registry; absent => inherit the thread customer. Lets an
  // employee text about multiple customers, or split a customer's addresses across
  // separate NetSuite accounts, one per preview tab.
  const [previewCustomerByKey, setPreviewCustomerByKey] = useState<Record<string, { id: number; name: string } | null>>({});
  // Staged line count per `${type}:${group}` — powers the remove-confirm guard
  // (confirm only when a tab actually holds lines).
  const [lineCountByKey, setLineCountByKey] = useState<Record<string, number>>({});
  // Types the human manually mounted via "+ Add preview" that the AI didn't
  // auto-detect — OR'd into the container gates so an empty new preview shows.
  const [manualMountTypes, setManualMountTypes] = useState<Record<PreviewType, boolean>>({ SO: false, RMA: false, CM: false });
  // Types that have a PERSISTED preview FORM in the preview_groups registry (created
  // by a human via "+ Add preview" or by Sally). This is what makes a preview a
  // durable FORM: the container mounts because a form EXISTS — independent of whether
  // any items were parsed from the conversation — and it SURVIVES close/reopen.
  const [registryTypes, setRegistryTypes] = useState<Record<PreviewType, boolean>>({ SO: false, RMA: false, CM: false });
  // "+ Add preview" dropdown open state.
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // The generated-messages section (SuggestedReply/OpenQuestion) self-hides when
  // empty; these track whether it currently has content so its header + quick-link
  // only appear when a generated message actually exists.
  const [hasSuggested, setHasSuggested] = useState(false);
  const [hasOpenQuestion, setHasOpenQuestion] = useState(false);
  // Per-section anchors for the quick-link "jump to" scrolling.
  const genRef = useRef<HTMLDivElement>(null);
  const orderSecRef = useRef<HTMLDivElement>(null);
  const rmaSecRef = useRef<HTMLDivElement>(null);
  const cmSecRef = useRef<HTMLDivElement>(null);
  const convoSecRef = useRef<HTMLDivElement>(null);
  // Order-edit flow (ORDER-EDIT-PLAN-v2): editable-orders dropdown, the selected
  // SO + its prefilled lines, and a SEPARATE status pair so editing an existing
  // order never clobbers the new-order create flow's feedback.
  const [editableOrders, setEditableOrders] = useState<any[]>([]);
  const [editSoId, setEditSoId] = useState<number | null>(null);
  const [editLines, setEditLines] = useState<any[] | null>(null);
  // EDIT mode: the NetSuite-auto-applied PROMOTION discount lines on the loaded SO
  // (getOrderPromoLines), passed to OrderItemEditor so genuine promos render as
  // read-only rows under their item. Display-only; refetched with the order lines.
  const [editPromoLines, setEditPromoLines] = useState<PromoLine[]>([]);
  // The SO's PRISTINE NetSuite lines (before any staged-edit overlay), captured at
  // load. editLines gets mutated in place by the overlay; this stays the untouched
  // baseline the editor diffs manual edits against, so persisted ops are always
  // relative to the real SO — never to an already-overlaid set.
  const [pristineEditLines, setPristineEditLines] = useState<any[] | null>(null);
  // EDIT mode: the SO's CURRENT NetSuite header (ship method/date/etc.) loaded at
  // select, so the editor seeds real values and the compare/revert have a baseline.
  // Sally's staged ship-method/ship-date change (a set_header op) is merged on top
  // to produce editHeaderSeed below (thread 4988).
  const [editHeaderBase, setEditHeaderBase] = useState<SoHeaderSeed | null>(null);
  // EDIT mode: the editor's LIVE current line set (via OrderItemEditor.onItemsChange),
  // so the compare panel reflects in-progress edits (incl. price) before they persist.
  // Null until the editor emits; the compare falls back to editLines meanwhile.
  const [liveEditItems, setLiveEditItems] = useState<any[] | null>(null);
  const [editStatus, setEditStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [editResult, setEditResult] = useState('');
  // Bumped by the "Reset to NetSuite" action to force the edit editor to remount
  // (drop its internal items/header/prefs) when reverting the SAME SO (the key,
  // otherwise keyed on editSoId alone, wouldn't change).
  const [editRevertNonce, setEditRevertNonce] = useState(0);
  // AI-prepared existing-order edit (feature #4 Option A). The backend stages a
  // pending edit per-thread; we auto-select its SO, overlay the proposed ops on
  // the loaded lines for review, show a banner, and resolve (applied/discarded)
  // after the human acts. pendingEdit holds the active staged row (or null).
  const [pendingEdit, setPendingEdit] = useState<any | null>(null);
  // ALL staged edits on this thread (one row per SO). Thread 4988: a single
  // message can stage edits against MULTIPLE SOs; each becomes a switchable
  // preview TAB. pendingEdit is the ACTIVE row (the SO the editor is mounted on).
  const [pendingEdits, setPendingEdits] = useState<any[]>([]);
  // Guards the one-time auto-stage: once we've auto-selected the pending edit's
  // SO and overlaid its ops, we don't re-apply on every effect/render.
  const stagedRef = useRef<number | null>(null);
  // Mirror of editSoId so the (poll-driven) pending-edit load effect can keep the
  // rep's ACTIVE staged-SO tab sticky across refetches without a stale closure.
  const editSoIdRef = useRef<number | null>(null);
  useEffect(() => { editSoIdRef.current = editSoId; }, [editSoId]);
  // Mirror of the active pending edit so persistence can read its staged set_header
  // op without churning the memoized persist callback on every poll.
  const pendingEditRef = useRef<any | null>(null);
  useEffect(() => { pendingEditRef.current = pendingEdit; }, [pendingEdit]);

  // A staged CANCELLATION is a pending_order_edits row whose proposed_edits is
  // [{"op":"cancel"}] (no item_id/quantity). We still auto-load the target SO so
  // the human sees what they're closing, but we DON'T overlay add/remove line
  // changes (there are none) and we show a distinct red/amber banner.
  const parseOps = (pe: any): any[] => {
    if (!pe) return [];
    try {
      return Array.isArray(pe.proposed_edits)
        ? pe.proposed_edits
        : JSON.parse(pe.proposed_edits || '[]');
    } catch (e) {
      return [];
    }
  };
  const pendingIsCancel = parseOps(pendingEdit).some((o: any) => o && o.op === 'cancel');

  // Closing the modal (isOpen false) also closes this sub-thread's reply composer.
  useEffect(() => {
    if (!isOpen) setShowReply(false);
  }, [isOpen]);

  const asc = [...messages].sort(
    (a, b) => msgTime(a) - msgTime(b) || (a.id - b.id)
  );
  const subject = asc.find(m => (m.thread_id ?? m.id) === m.id) || asc[0];
  // ── Collapsible details sections ────────────────────────────────────────────
  // Each details section can be minimized to tame a busy view. Collapse state
  // persists per thread in localStorage (a visual pref the rep set should stick
  // across reopen). Sections are HIDDEN via CSS (not unmounted) so a collapsed
  // preview keeps its unsaved edits.
  const _collapseKey = `slx_collapsed_${subject.thread_id ?? subject.id}`;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(_collapseKey) || '{}') || {}; } catch { return {}; }
  });
  const toggleSection = (key: string) => setCollapsed(c => {
    const next = { ...c, [key]: !c[key] };
    try { localStorage.setItem(_collapseKey, JSON.stringify(next)); } catch { /* non-fatal */ }
    return next;
  });
  const intent = subject.intent || 'UNKNOWN';
  // Primary order-message detection: an inbound message the feed already knows is
  // an order (intent=ORDER) or that carries inline items. RMA (returns) messages
  // are EXCLUDED here — they drive the RMA preview (rmaMsg below), not the SO one.
  const orderMsgByIntent = asc.find(
    m => m.direction !== 'outbound' && m.intent !== 'RMA'
      && (m.intent === 'ORDER' || (m.items && m.items.length > 0))
  );
  // Primary RMA-message detection: an inbound message parsed as a RETURN (RMA).
  const rmaMsgByIntent = asc.find(
    m => m.direction !== 'outbound' && m.intent === 'RMA'
  );
  // Fallback anchor for IMAGE/INFO orders (Phase 6.2): a customer texts a PHOTO of
  // an order — the caption intent is INFO, and the feed row carries no inline
  // items, so orderMsgByIntent misses it. But the backend still transcribes the
  // photo into thread message_items, which getThreadOrderItems (thread scope)
  // returns. When that happens (threadHasItems below), we still want the SO-preview
  // panel to show. Anchor it on the best inbound message: prefer one with an image
  // attachment (the photo itself), then any inbound, then the subject — so the
  // panel's messageId/customer_id/raw_message wiring stays valid.
  const orderAnchorFallback =
    asc.find(m => m.direction !== 'outbound' && (m.media && m.media.length > 0)) ||
    asc.find(m => m.direction !== 'outbound') ||
    subject;
  // getThreadOrderItems (intent-agnostic) returned >0 lines for this open thread —
  // an image-sourced order grounds items even though no message reads as ORDER.
  const [threadHasItems, setThreadHasItems] = useState(false);
  // Resolved order message: the intent/inline-items hit, else the image/INFO
  // fallback once we know the thread actually has order items. Everything
  // downstream (handleCreateSO, editableOrders, the panel gate) keys off this.
  const orderMsg = orderMsgByIntent || ((threadHasItems || registryTypes.SO || manualMountTypes.SO) ? orderAnchorFallback : undefined);
  // RMA (returns) preview anchor: the RMA-intent message, else the same inbound
  // fallback once the thread is known to carry RMA lines (getThreadRmaItems > 0).
  const [threadHasRmaItems, setThreadHasRmaItems] = useState(false);
  const rmaMsg = rmaMsgByIntent || ((threadHasRmaItems || registryTypes.RMA || manualMountTypes.RMA) ? orderAnchorFallback : undefined);
  // Credit Memo (CM) preview anchor: a CREDIT thread. Detection is the durable
  // is_credit_request flag (threadIsCredit) OR the thread already carrying started
  // CM lines (threadHasCmItems) — so a CM in progress persists even if the flag
  // probe lags. Anchored on the same inbound fallback as SO/RMA. Independent of
  // orderMsg/rmaMsg: a thread could carry more than one preview.
  const [threadIsCredit, setThreadIsCredit] = useState(false);
  const [threadHasCmItems, setThreadHasCmItems] = useState(false);
  const creditMsg = (threadIsCredit || threadHasCmItems || registryTypes.CM || manualMountTypes.CM) ? orderAnchorFallback : undefined;
  const escalated = subject.status === 'ESCALATED';

  // ── 8x8 CALL thread (channel='CALL') ────────────────────────────────────────
  // A call surfaces in the feed as ONE message row (the ~300-char snippet); the
  // conversation BODY is hydrated on open from the surviving detail endpoint —
  // GET /calltranscripts/{id}, id = external_id minus the 'call:' prefix. Its
  // segments are synthesized into Message-SHAPED objects and rendered through
  // the SAME MessageCard bubble map as SMS/email (identical styling by
  // construction — no parallel renderer). The synthesized rows live ONLY in
  // this local state / the bubble map: they never enter the messages prop,
  // status handlers, or reassign paths (and carry NEGATIVE ids so they can
  // never collide with — or be mistaken for — real message rows).
  // Text-only affordances (reply, move-message, AI draft/prompt) are hidden:
  // you can't text a call. The status workflow stays fully functional.
  const isCallThread = subject.channel === 'CALL';

  // "Awaiting reply" — the at-a-glance "has this thread been responded to?" signal
  // the team was missing. True when the MOST RECENT message in the sub-thread is
  // INBOUND (from the customer) and nothing (rep reply / approved AI draft) has
  // gone out after it. Excluded on CALL threads (a transcript isn't an open text
  // conversation) and on COMPLETED threads (already handled). `asc` is chronological,
  // so the tail is the freshest message.
  const lastMsg = asc[asc.length - 1];
  const awaitingReply =
    !isCallThread &&
    subject.status !== 'COMPLETED' &&
    !!lastMsg &&
    lastMsg.direction !== 'outbound';

  // Both prefixes resolve to the SAME call_transcripts id — a voicemail is an
  // ordinary row in that table, so GET /calltranscripts/{id} serves it too.
  const callCtId = isCallThread
    ? parseInt((subject.external_id || subject.sender_id || '').replace(/^(?:call|vm):/, ''), 10)
    : NaN;
  const isVoicemailThread = isCallThread && isVoicemailMessage(subject);
  const [callDetail, setCallDetail] = useState<CallTranscript | null>(null);
  const [callLoading, setCallLoading] = useState(false);
  const [callError, setCallError] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen || !isCallThread) return;
    if (!Number.isFinite(callCtId)) { setCallError('Bad call reference'); return; }
    let cancelled = false;
    setCallLoading(true);
    setCallError(null);
    getCallTranscript(callCtId)
      .then(d => { if (!cancelled) setCallDetail(d); })
      .catch(err => {
        console.error('[call-transcript] load failed', err);
        if (!cancelled) setCallError('Failed to load the call transcript');
      })
      .finally(() => { if (!cancelled) setCallLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, isCallThread, callCtId]);

  // Segments -> Message-shaped bubbles. Speaker maps to direction (rep ->
  // outbound / customer+other -> inbound); the segment label + [mm:ss] offset
  // go through MessageCard's REAL sender-label props (sent_by for outbound,
  // customer_name for inbound) — no style hacks. received_at stays the call's
  // own timestamp so the time chip reads like any other bubble.
  const callMessages: Message[] = (callDetail?.segments && Array.isArray(callDetail.segments) ? callDetail.segments : [])
    .map((s, i) => {
      const label = s.t && s.t !== '--:--' ? `${s.label} [${s.t}]` : s.label;
      return {
        ...subject,
        id: -(i + 1),                 // synthetic, never collides with real ids
        direction: (s.speaker === 'rep' ? 'outbound' : 'inbound') as Message['direction'],
        sent_by: label,               // outbound sender slot
        customer_name: label,         // inbound sender slot
        raw_message: s.text,
        channel: 'CALL' as Message['channel'],
        items: undefined,
        needs_human_review: false,
        comment_count: undefined,
      };
    });

  // Freshest change signal across the whole sub-thread: when any message updates
  // (e.g. a new outbound arrives or a draft is generated), this string changes
  // and SuggestedReply refetches — same approach as OrderItemEditor's refreshKey.
  const threadRefreshKey = asc.reduce(
    (max, m) => (m.updated_at > max ? m.updated_at : max),
    subject.updated_at
  );

  // Employee-originated thread support (#7): a rep texting on behalf of a
  // customer. Detected by the additive is_employee flag, or the legacy convention
  // where customer_name is prefixed "[EMPLOYEE]". Only employee threads render the
  // target-customer confirm dropdown — normal customer threads are untouched.
  const subjEmp = subject as Message;
  const isEmployeeThread =
    subjEmp.is_employee === true ||
    /^\s*\[employee\]/i.test(subject.customer_name || '');
  // WHO the rep is. Prefer the durable employee_sender_name (present for the
  // whole thread, even after the target customer resolves). Fall back to parsing
  // the legacy "[EMPLOYEE] <name>" prefix off customer_name for old rows that
  // predate the field. The badge reads "Rep: <name>" when we have a name, else a
  // bare "Employee".
  const repNameFromPrefix = (() => {
    const m = /^\s*\[employee\]\s*(.*)$/i.exec(subject.customer_name || '');
    const name = ((m && m[1]) || '').trim();
    return name || '';
  })();
  const employeeRepName = (subjEmp.employee_sender_name || '').trim() || repNameFromPrefix;
  const employeeBadgeText = employeeRepName ? `Rep: ${employeeRepName}` : 'Employee';
  // Reply recipient label. We reply to WHOEVER messaged us — the thread subject's
  // sender (the server sends to subject.sender_id) — NOT the customer being
  // discussed. On an employee/rep-on-behalf thread the sender is the rep (e.g.
  // "Matt Askew") while customer_name is the customer they wrote about (e.g.
  // "Co-Lin Feed & Seed"), so showing customer_name here misled the team into
  // thinking the reply went to the customer. Name the rep on an employee thread;
  // otherwise the sender IS the customer, so the customer name is correct (falling
  // back to the raw sender_id email/phone when unnamed).
  const contact = (isEmployeeThread && employeeRepName)
    ? employeeRepName
    : (subject.customer_name || subject.sender_id);
  // Auto-fill value = the RESOLVED target (customer_id/customer_name) when set.
  const targetValue =
    subject.customer_id != null
      ? { id: subject.customer_id, name: subject.customer_name || '' }
      : null;
  // Candidates = the AI's determinations, preloaded as suggestions.
  const targetCandidates = subjEmp.target_customer_candidates || [];
  const targetUnresolved = subjEmp.target_customer_unresolved === true;

  // Confirm/override the target customer, then refresh so the field reflects the
  // saved target (value follows the message's customer_id on the next poll). The
  // set call SURFACES errors; we log and rely on the next poll rather than
  // optimistically pretending it saved.
  const handleConfirmCustomer = async (c: { id: number; name: string }) => {
    try {
      await setMessageTargetCustomer(subject.id, c.id, c.name);
      onAfterSend();
    } catch (err: any) {
      /* auth failures toast via the api interceptor; surface the rest here so a
         failed customer set isn't silent (it otherwise just reverts on poll). */
      console.warn('[target-customer] set failed', err);
      const st = err?.response?.status;
      if (st !== 401 && st !== 403) showToast(err?.response?.data?.error || 'Could not set the customer — please try again.');
    }
  };

  // Toggle the human-review flag on this sub-thread, then refresh so the badge
  // and SignalR push propagate. Non-fatal: a failure just surfaces on next poll.
  const handleFlagReview = async (value: boolean) => {
    setFlagBusy(true);
    try {
      await setNeedsReview(subject.id, value);
      onAfterSend();
    } catch (err: any) {
      const st = err?.response?.status;
      if (st !== 401 && st !== 403) showToast(err?.response?.data?.error || 'Could not update the review flag — please try again.');
    } finally {
      setFlagBusy(false);
    }
  };

  // "Ask next step": FORCE the AI to read this thread and draft the next pending
  // question (customer -> item -> quantity), bypassing the human-presence
  // suppression because the rep explicitly asked. Refresh so the new draft shows.
  const handleAdvance = async () => {
    setAdvanceBusy(true);
    // Refetch the order preview IMMEDIATELY (the thread sweep is ~1s) so the rep
    // sees the items right away instead of staring at "Prompting..." while the
    // brain's multi-step AI reasoning runs. We bump AGAIN after it returns to pick
    // up the generated draft + any resolution changes.
    setAdvanceNonce(n => n + 1);
    try {
      await advanceThread(subject.thread_id ?? subject.id);
      setAdvanceNonce(n => n + 1);
      onAfterSend();
    } catch (err) {
      /* surfaced on next poll */
    } finally {
      setAdvanceBusy(false);
    }
  };

  // Effective customer for one preview TAB: the per-preview override if set, else
  // the thread's customer (from that type's anchor message). Item resolution +
  // create both key on this so a re-pointed preview builds against the right account.
  const custFor = (type: PreviewType, group: number): { id: number | null; name: string | null } => {
    const o = previewCustomerByKey[`${type}:${group}`];
    if (o) return { id: o.id, name: o.name };
    const anchor = type === 'CM' ? creditMsg : (type === 'RMA' ? rmaMsg : orderMsg);
    return { id: anchor?.customer_id ?? null, name: anchor?.customer_name ?? null };
  };

  // Create ONE Sales Order for a single preview TAB's lines (a thread can carry
  // multiple order previews). Status/result tracked per group; uses THAT tab's
  // customer (per-preview override else thread customer); passes the group so the
  // backend conversion gate scopes to this tab only.
  const handleCreateSO = async (group: number, editedItems?: any[], hotLoad?: boolean, header?: any, weightLb?: number) => {
    if (!orderMsg) return;
    const cust = custFor('SO', group);
    const setStatus = (s: 'idle' | 'loading' | 'success' | 'error') =>
      setSoStatusByGroup(prev => ({ ...prev, [group]: s }));
    const setResult = (r: string) => setSoResultByGroup(prev => ({ ...prev, [group]: r }));
    if (!cust.id) {
      setStatus('error');
      setResult('No customer linked to this preview');
      return;
    }
    setStatus('loading');
    try {
      const items = editedItems || (orderMsg.items && orderMsg.items.length > 0
        ? orderMsg.items
        : await getMessageItems(orderMsg.id));
      const resolvedItems = items.filter((i: any) => i.item_id);
      if (resolvedItems.length === 0) {
        setStatus('error');
        setResult('No resolved items to create SO from');
        return;
      }
      const result = await createSalesOrder(
        orderMsg.id,
        cust.id,
        resolvedItems,
        'Generated from SalesLogix — thread #' + (subject.thread_id ?? subject.id),
        hotLoad,
        header,
        weightLb,
        group,
      );
      setStatus('success');
      setResult('SO #' + (result.salesorder_number || result.salesorder_id || 'created') + ' — receipt drafted for review');
      // Do NOT auto-complete the thread — a person marks it completed manually.
      // Refresh so the AI's "order placed" receipt draft appears in Suggested Reply.
      setAdvanceNonce(n => n + 1);
      onAfterSend();
    } catch (err: any) {
      setStatus('error');
      setResult(err?.response?.data?.error || err.message || 'Failed to create SO');
    }
  };

  // Create a NetSuite Return Authorization (RMA) from the resolved return lines.
  // Mirrors handleCreateSO: guards a linked customer, filters to resolved lines,
  // calls createRMA (write-gated server-side -> 403 {readonly:true} until go-live,
  // surfaced as the error message). Does NOT auto-complete the thread.
  // Create ONE Return Authorization for a single RMA group's lines (a thread can
  // require several). Status/result are tracked per group so each preview shows its
  // own feedback. Write-gated server-side (403 {readonly:true} until go-live).
  const handleCreateRMA = async (group: number, editedItems?: any[]) => {
    if (!rmaMsg) return;
    const setStatus = (s: 'idle' | 'loading' | 'success' | 'error') =>
      setRmaStatusByGroup(prev => ({ ...prev, [group]: s }));
    const setResult = (r: string) =>
      setRmaResultByGroup(prev => ({ ...prev, [group]: r }));
    const cust = custFor('RMA', group);
    if (!cust.id) {
      setStatus('error'); setResult('No customer linked to this preview');
      return;
    }
    setStatus('loading');
    try {
      const items = editedItems || [];
      const resolvedItems = items.filter((i: any) => i.item_id);
      if (resolvedItems.length === 0) {
        setStatus('error'); setResult('No resolved items to create an RMA from');
        return;
      }
      const result = await createRMA(
        rmaMsg.id,
        cust.id,
        resolvedItems,
        '[CommLogix RMA] ' + (rmaMsg.raw_message || '').substring(0, 200)
      );
      setStatus('success');
      setResult('RMA #' + (result.rma_number || result.rma_id || 'created') + ' — return authorization drafted for review');
      setAdvanceNonce(n => n + 1);
      onAfterSend();
    } catch (err: any) {
      setStatus('error');
      setResult(err?.response?.data?.error || err.message || 'Failed to create RMA');
    }
  };

  // Move a return line to another RMA group (or a new one) — the per-line "RMA #"
  // picker. PATCHes rma_group, then bumps the preview so the line relocates and a
  // new group's preview appears. Non-fatal.
  const handleMoveRmaLine = async (lineId: number, toGroup: number) => {
    if (!rmaMsg) return;
    invalidateDetect();  // drop any in-flight sweep so it can't revert the move
    try {
      await moveRmaLine(rmaMsg.id, lineId, toGroup);
      setRmaGroups(prev => (prev.includes(toGroup) ? prev : [...prev, toGroup].sort((a, b) => a - b)));
      setAdvanceNonce(n => n + 1);
      refreshDetect();  // reconcile against the committed line groups at once
    } catch (err) {
      console.error('Failed to move RMA line:', err);
    }
  };

  // Create a standalone NetSuite Credit Memo from the CM preview. Mirrors
  // handleCreateRMA: HARD customer guard (also enforced server-side), calls
  // createCreditMemo (write-gated -> 403 {readonly:true} if writes were disabled,
  // surfaced as the error). Go-live is executed, so this creates a REAL credit memo.
  const handleCreateCM = async (group: number, items: MessageItem[], header: CmHeaderFields, memo: string) => {
    if (!creditMsg) return;
    const cust = custFor('CM', group);
    const setStatus = (s: 'idle' | 'loading' | 'success' | 'error') =>
      setCmStatusByGroup(prev => ({ ...prev, [group]: s }));
    const setResult = (r: string) => setCmResultByGroup(prev => ({ ...prev, [group]: r }));
    if (!cust.id) {
      setStatus('error'); setResult('No customer linked — assign a customer to create a credit memo.');
      return;
    }
    const resolved = items.filter(i => i.item_id);
    if (resolved.length === 0) {
      setStatus('error'); setResult('Add a credit line with an item and amount.');
      return;
    }
    setStatus('loading'); setResult('');
    try {
      const composedMemo = memo && memo.trim()
        ? memo.trim()
        : '[CommLogix Credit] ' + (creditMsg.raw_message || '').substring(0, 200);
      const result = await createCreditMemo(
        creditMsg.id, cust.id, resolved, composedMemo, header);
      setStatus('success');
      setResult('Credit memo ' + (result.cm_number || result.cm_id || 'created') + ' created in NetSuite.');
      setAdvanceNonce(n => n + 1);
      onAfterSend();
    } catch (err: any) {
      setStatus('error');
      setResult(err?.response?.data?.error || err.message || 'Failed to create credit memo');
    }
  };

  // ── Multi-preview add / remove / re-customer handlers ───────────────────────
  const anchorFor = (type: PreviewType) =>
    (type === 'CM' ? creditMsg : (type === 'RMA' ? rmaMsg : orderMsg)) || orderAnchorFallback;

  // "+ Add preview": create a NEW empty preview instance (tab) of a type. Registers
  // the next group server-side (so it survives refetch), mounts the container if the
  // AI hadn't auto-detected it, and selects the new tab. Non-fatal.
  const handleAddPreview = async (type: PreviewType) => {
    const anchor = anchorFor(type);
    if (!anchor) return;
    invalidateDetect();  // drop any in-flight sweep so it can't undo this add
    try {
      const { group_no } = await createPreviewGroup(anchor.id, type);
      const addSel = (setGroups: React.Dispatch<React.SetStateAction<number[]>>,
                      setActive: React.Dispatch<React.SetStateAction<number>>) => {
        setGroups(prev => Array.from(new Set([...(prev.length ? prev : [1]), group_no])).sort((a, b) => a - b));
        setActive(group_no);
      };
      if (type === 'SO') addSel(setSoGroups, setActiveSoGroup);
      else if (type === 'RMA') addSel(setRmaGroups, setActiveRmaGroup);
      else addSel(setCmGroups, setActiveCmGroup);
      setManualMountTypes(prev => ({ ...prev, [type]: true }));
      setAdvanceNonce(n => n + 1);
      refreshDetect();  // reconcile against the committed registry at once
    } catch (err) {
      console.error('Failed to add preview:', err);
    }
  };

  // Remove (×) a preview instance — STAGING-ONLY (never cancels a NetSuite doc).
  // Confirms only when the tab holds staged lines. Deletes the group server-side,
  // drops it locally, re-selects an adjacent tab, and collapses the container when
  // it was the last tab of that type.
  const handleRemovePreview = async (type: PreviewType, group: number) => {
    const anchor = anchorFor(type);
    if (!anchor) return;
    const label = type === 'CM' ? 'Credit Memo' : (type === 'RMA' ? 'RMA' : 'Order');
    const hasLines = (lineCountByKey[`${type}:${group}`] || 0) > 0;
    if (hasLines && !window.confirm(
      `Remove this ${label} preview? This clears the staged preview and does NOT cancel any NetSuite order.`)) {
      return;
    }
    invalidateDetect();  // drop any in-flight sweep so it can't re-add this tab
    try {
      await deletePreviewGroup(anchor.id, type, group);
    } catch (err) {
      console.error('Failed to remove preview:', err);
      return;
    }
    const apply = (groups: number[], active: number,
                   setGroups: React.Dispatch<React.SetStateAction<number[]>>,
                   setActive: React.Dispatch<React.SetStateAction<number>>) => {
      const remaining = groups.filter(g => g !== group);
      setGroups(remaining);
      if (active === group) {
        const idx = groups.indexOf(group);
        const adj = remaining[idx - 1] ?? remaining[idx] ?? remaining[0] ?? 1;
        setActive(adj);
      }
      if (remaining.length === 0) setManualMountTypes(prev => ({ ...prev, [type]: false }));
    };
    if (type === 'SO') apply(soGroups, activeSoGroup, setSoGroups, setActiveSoGroup);
    else if (type === 'RMA') apply(rmaGroups, activeRmaGroup, setRmaGroups, setActiveRmaGroup);
    else apply(cmGroups, activeCmGroup, setCmGroups, setActiveCmGroup);
    // Drop this tab's per-preview customer + line-count entries.
    setPreviewCustomerByKey(prev => { const n = { ...prev }; delete n[`${type}:${group}`]; return n; });
    setLineCountByKey(prev => { const n = { ...prev }; delete n[`${type}:${group}`]; return n; });
    // Removing the ORDER preview must also clear any staged EDIT — otherwise a stale
    // pending_order_edits row re-mounts the SO editor in edit mode on reopen (the
    // "removed tab comes back" bug). setThreadEditTarget(null) discards the thread's
    // pending edits AND arms the NEW-ORDER LOCK server-side (so the AI won't re-stage);
    // clear the local edit state to match. The lock lifts the moment a human re-picks
    // an existing order in the dropdown.
    if (type === 'SO') {
      const rootId = subject.thread_id ?? subject.id;
      setThreadEditTarget(rootId, null).catch(() => { /* non-fatal */ });
      setPendingEdit(null);
      stagedRef.current = null;
      setEditSoId(null);
      setEditLines(null);
      setPristineEditLines(null);
    }
    setAdvanceNonce(n => n + 1);
    refreshDetect();  // reconcile against the post-delete registry at once
  };

  // Reassign ONE preview's customer (defaults to the thread customer). Persists +
  // re-resolves that preview's lines server-side, then bumps the nonce so its editor
  // refetches against the new customer. Non-fatal.
  const handleChangePreviewCustomer = async (type: PreviewType, group: number, c: { id: number; name: string }) => {
    const anchor = anchorFor(type);
    if (!anchor) return;
    invalidateDetect();  // drop any in-flight sweep so its stale customer can't win
    try {
      await setPreviewCustomer(anchor.id, type, group, c.id, c.name);
      setPreviewCustomerByKey(prev => ({ ...prev, [`${type}:${group}`]: c }));
      setAdvanceNonce(n => n + 1);
      refreshDetect();  // reconcile against the committed registry customer at once
    } catch (err) {
      console.error('Failed to set preview customer:', err);
    }
  };

  // The per-preview CUSTOMER selector shown under each container's tab strip. Seeds
  // from the tab's effective customer (override else thread customer); picking one
  // re-points THIS preview and re-resolves its lines (handleChangePreviewCustomer).
  const previewCustomerRow = (type: PreviewType, group: number) => {
    const cur = custFor(type, group);
    // Is THIS preview pointed at a DIFFERENT customer than the thread? The per-preview
    // override is intentionally separate from the thread customer (an employee can text
    // about several customers, one preview each). When they differ the thread header
    // still shows the THREAD customer, which read as "the change didn't take" — so make
    // the override explicit right on the preview: a clear badge naming who THIS order
    // is for, plus the thread customer it overrides. custFor already returns the
    // override when set; compare it to the anchor (thread) customer to detect it.
    const override = previewCustomerByKey[`${type}:${group}`];
    const anchor = anchorFor(type);
    const threadCustId = anchor?.customer_id ?? null;
    const threadCustName = anchor?.customer_name ?? null;
    const isOverride = !!(override && override.id != null && override.id !== threadCustId);
    const noun = type === 'CM' ? 'credit memo' : (type === 'RMA' ? 'RMA' : 'order');
    return (
      <div className="mb-2 space-y-1">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 whitespace-nowrap">Preview customer:</label>
          <CustomerConfirmSelect
            value={cur.id != null ? { id: cur.id, name: cur.name || '' } : null}
            onSelect={(c) => handleChangePreviewCustomer(type, group, { id: c.id, name: c.name })}
            placeholder="Customer for this preview…"
            className="text-sm border border-gray-300 rounded px-2 py-1 text-gray-700 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        {isOverride && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs pl-0.5">
            <span
              title={`This ${noun} preview is set to ${cur.name}, which is different from the thread's customer. That's intentional — only this preview uses this account.`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-100 text-brand-800 font-semibold"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              This {noun} → {cur.name}
            </span>
            {threadCustName && (
              <span className="text-gray-400">overrides thread customer <span className="text-gray-500">{threadCustName}</span></span>
            )}
          </div>
        )}
      </div>
    );
  };

  // A section's collapse header: a chevron + title button that shows/hides the
  // section. Rendered ONLY for a section that exists (the caller gates it).
  const sectionHeader = (key: string, title: string) => (
    <button
      type="button"
      onClick={() => toggleSection(key)}
      className="w-full flex items-center gap-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 hover:text-brand-700 py-1"
      title={collapsed[key] ? `Show ${title}` : `Hide ${title}`}
    >
      <svg className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${collapsed[key] ? '-rotate-90' : ''}`}
           fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
      {title}
    </button>
  );

  // Smooth-scroll a section to the top of the modal's scroll viewport.
  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // The sections that EXIST on this thread, in view order — powers the always-on
  // quick-link "Jump to" bar. Generated messages appear only when there's content;
  // the conversation always exists once messages have loaded.
  const generatedExists = !isCallThread && (hasSuggested || hasOpenQuestion);
  const conversationExists = isCallThread ? callMessages.length > 0 : asc.length > 0;
  const detailSections = [
    { key: 'generated', label: 'Generated messages', exists: generatedExists, ref: genRef },
    { key: 'order', label: 'Order preview', exists: !!orderMsg, ref: orderSecRef },
    { key: 'rma', label: 'RMA preview', exists: !!rmaMsg, ref: rmaSecRef },
    { key: 'cm', label: 'Credit memo', exists: !!creditMsg, ref: cmSecRef },
    { key: 'conversation', label: 'Conversation', exists: conversationExists, ref: convoSecRef },
  ].filter(s => s.exists);

  // Load this customer's editable orders once the sub-thread is open and we have
  // an order message with a linked customer. Failure leaves the list empty (the
  // dropdown just doesn't render) — non-fatal.
  const orderCustomerId = orderMsg?.customer_id ?? null;
  useEffect(() => {
    if (!isOpen || !orderCustomerId) { setEditableOrders([]); return; }
    let cancelled = false;
    getEditableOrders(orderCustomerId)
      .then(orders => { if (!cancelled) setEditableOrders(orders || []); })
      .catch(() => { if (!cancelled) setEditableOrders([]); });
    return () => { cancelled = true; };
  }, [isOpen, orderCustomerId]);

  // Multi-preview detection (ONE consolidated probe). When the sub-thread is open,
  // fetch every preview type's thread lines + the durable credit flag + the tab
  // REGISTRY in parallel, then derive, for each type: whether it mounts (threadHas*
  // / threadIsCredit), the distinct GROUPS/tabs (line-derived canonical group —
  // rma_group for RMA, preview_group for SO/CM, NULL => 1 — UNIONed with registry
  // rows so a manually-added EMPTY tab still shows), the per-tab line COUNT (powers
  // the remove-confirm guard), and the per-preview CUSTOMER overrides. Runs even
  // when a message already reads as ORDER/RMA so multi-group threads still learn
  // their tabs (#4041). Re-checks on thread updates + the open-tick. Fully
  // NON-FATAL — any probe failing just leaves that type hidden/empty.
  useEffect(() => {
    // Run for ANY open thread — INCLUDING call threads. A CALL thread can carry an
    // order/RMA/CM preview (an order placed over the phone), and this sweep is what
    // loads + PERSISTS each preview's per-tab customer, groups and line counts. It
    // was previously gated out for call threads (`|| isCallThread`), so on a call
    // thread the effect early-returned every tick and reset previewCustomerByKey to
    // {} — silently discarding a re-pointed preview customer so it snapped back to
    // the thread customer (thread #5015). The sweep endpoints are all thread-scoped
    // and channel-agnostic, so running them for call threads is safe and correct.
    if (!isOpen) {
      setThreadHasItems(false); setThreadHasRmaItems(false);
      setThreadIsCredit(false); setThreadHasCmItems(false);
      setRegistryTypes({ SO: false, RMA: false, CM: false });
      setSoGroups([]); setRmaGroups([]); setCmGroups([]);
      setLineCountByKey({}); setPreviewCustomerByKey({});
      detectionRootRef.current = null;
      return;
    }
    const root = subject.thread_id ?? subject.id;
    // Claim this root as current. Results below apply only if we're still on it +
    // mounted — NOT gated on a per-run flag, so a sweep that outlives the 2s openTick
    // still lands (that race was leaving RMA/CM previews permanently hidden).
    detectionRootRef.current = root;
    // Snapshot the mutation seq as this sweep FIRES; a mutation that lands before
    // the sweep resolves advances the seq and invalidates this (now-stale) response.
    const seqAtFire = mutationSeqRef.current;
    const canon = (l: any, isRma: boolean): number => {
      const g = isRma ? l.rma_group : l.preview_group;
      return g == null ? 1 : Number(g);
    };
    // ERROR-PRESERVING: each poll (openTick) re-probes; a TRANSIENT failure of any
    // one call must NOT flip a mounted preview off (that was the "credit memo pops in
    // and out" flicker). So we tag each result ok/failed and only update a piece of
    // state when its own fetch SUCCEEDED — a failed call leaves the prior value intact.
    const wrap = <T,>(p: Promise<T>) => p.then(v => ({ ok: true, v })).catch(() => ({ ok: false, v: undefined as any }));
    Promise.all([
      wrap(getThreadOrderItems(root)),
      wrap(getThreadRmaItems(root)),
      wrap(getThreadCmItems(root)),
      wrap(getThreadIsCredit(root)),
      wrap(listPreviewGroups(root)),
    ]).then(([soR, rmaR, cmR, credR, regR]) => {
      if (!detectionMountedRef.current || detectionRootRef.current !== root) return;
      // Stale-sweep guard: a local preview mutation happened after this fetch
      // fired, so its results are pre-mutation — drop them so they can't clobber
      // the optimistic change. The fresh sweep (detectNonce) carries the truth.
      if (mutationSeqRef.current !== seqAtFire) return;
      const reg: any[] = regR.ok && Array.isArray(regR.v) ? regR.v : [];
      const regHas = (t: PreviewType) => reg.some(r => (r.line_kind || 'SO').toUpperCase() === t);
      // Mount flags — only updated on success (never unmount on a transient error).
      if (soR.ok) setThreadHasItems((Array.isArray(soR.v) ? soR.v : []).length > 0);
      if (rmaR.ok) setThreadHasRmaItems((Array.isArray(rmaR.v) ? rmaR.v : []).length > 0);
      if (cmR.ok) setThreadHasCmItems((Array.isArray(cmR.v) ? cmR.v : []).length > 0);
      if (credR.ok) setThreadIsCredit(!!credR.v);
      if (regR.ok) setRegistryTypes({ SO: regHas('SO'), RMA: regHas('RMA'), CM: regHas('CM') });
      // Distinct tabs per type = line-derived groups UNIONed with registry groups.
      // Recomputed only for a type whose LINE fetch succeeded (else keep prior tabs).
      const groupsFor = (lines: MessageItem[], isRma: boolean, type: PreviewType) => {
        const counts: Record<number, number> = {};
        (Array.isArray(lines) ? lines : []).forEach(l => { const g = canon(l, isRma); counts[g] = (counts[g] || 0) + 1; });
        if (regR.ok) reg.filter(r => (r.line_kind || 'SO').toUpperCase() === type).forEach(r => {
          const g = Number(r.group_no || 1); if (!(g in counts)) counts[g] = 0;
        });
        const groups = Object.keys(counts).map(Number).sort((a, b) => a - b);
        return { groups, counts };
      };
      const lcUpdates: Record<string, number> = {};
      if (soR.ok) { const g = groupsFor(soR.v, false, 'SO'); setSoGroups(g.groups); g.groups.forEach(x => { lcUpdates['SO:' + x] = g.counts[x] || 0; }); }
      if (rmaR.ok) { const g = groupsFor(rmaR.v, true, 'RMA'); setRmaGroups(g.groups); g.groups.forEach(x => { lcUpdates['RMA:' + x] = g.counts[x] || 0; }); }
      if (cmR.ok) { const g = groupsFor(cmR.v, false, 'CM'); setCmGroups(g.groups); g.groups.forEach(x => { lcUpdates['CM:' + x] = g.counts[x] || 0; }); }
      if (Object.keys(lcUpdates).length) setLineCountByKey(prev => ({ ...prev, ...lcUpdates }));
      // Per-preview customer overrides — MERGE from the registry on success (never
      // wipe on error, and never clobber an optimistic pick if the registry lags).
      if (regR.ok) {
        setPreviewCustomerByKey(prev => {
          const next = { ...prev };
          reg.forEach(r => {
            if (r.customer_id != null) {
              const t = (r.line_kind || 'SO').toUpperCase();
              next[t + ':' + Number(r.group_no || 1)] = { id: Number(r.customer_id), name: r.customer_name || '' };
            }
          });
          return next;
        });
      }
    });
  }, [isOpen, isCallThread, subject.thread_id, subject.id, threadRefreshKey, openTick, detectNonce]);

  // Clamp each type's active tab into its available groups (after add/remove/refetch).
  useEffect(() => { if (soGroups.length && !soGroups.includes(activeSoGroup)) setActiveSoGroup(soGroups[0]); }, [soGroups, activeSoGroup]);
  useEffect(() => { if (rmaGroups.length && !rmaGroups.includes(activeRmaGroup)) setActiveRmaGroup(rmaGroups[0]); }, [rmaGroups, activeRmaGroup]);
  useEffect(() => { if (cmGroups.length && !cmGroups.includes(activeCmGroup)) setActiveCmGroup(cmGroups[0]); }, [cmGroups, activeCmGroup]);


  // Selecting an order from the dropdown: fetch its current lines and mount the
  // editor in 'edit' mode prefilled with them. Clearing the selection unmounts
  // the edit editor and restores the normal new-order create path.
  // `persist` = this is a genuine USER pick from the dropdown (persist the lock so
  // it survives reload + Prompt AI targets it). The programmatic auto-select from
  // a loaded pending edit passes persist=false (the pending row already implies the
  // target). Part B: the persisted lock is the order-level thread customer-lock.
  const handleSelectEditOrder = async (soId: number | null, persist: boolean = false) => {
    setEditStatus('idle');
    setEditResult('');
    setEditLines(null);
    setPristineEditLines(null);
    setLiveEditItems(null);
    setEditHeaderBase(null);
    setEditSoId(soId);
    if (persist) {
      // Fire-and-forget: the pick already applied locally; a persistence failure
      // must never block the editor. Include the tranid for display/logging.
      const rootId = subject.thread_id ?? subject.id;
      const tranid = editableOrders.find((o: any) => Number(o.id) === Number(soId))?.tranid;
      setThreadEditTarget(rootId, soId, tranid).catch(() => { /* non-fatal */ });
    }
    if (!soId) {
      setEditPromoLines([]);
      // Switching to "New order" is a DURABLE human decision. The backend
      // (setThreadEditTarget with so_id=null, fired above when persist) discards any
      // staged pending edit AND arms the NEW-ORDER LOCK, so the AI won't re-stage an
      // edit. Clear the local pending-edit state too so the banner/edit editor
      // disappears at once and the pending-edit auto-select effect can't re-drive
      // edit mode. Only on a genuine user pick (persist) — never a programmatic restore.
      if (persist) { setPendingEdit(null); stagedRef.current = null; }
      return;
    }
    try {
      const lines = await getSalesOrderLines(soId);
      setEditLines(lines || []);
      // Snapshot the untouched baseline (overlay mutates editLines, not this).
      setPristineEditLines(lines || []);
      // Load the SO's CURRENT header (ship method/date/etc.) so the editor seeds
      // real values and Sally's staged header change highlights against them.
      // Non-fatal: a failure just leaves header fields at their editor defaults.
      getSalesOrderHeader(soId)
        .then(setEditHeaderBase)
        .catch(() => setEditHeaderBase({}));
      // NetSuite-auto-applied promotions for this order (read-only display rows).
      // Non-fatal: a failure just means no promo rows show — never blocks the edit.
      getOrderPromoLines(soId)
        .then(setEditPromoLines)
        .catch(() => setEditPromoLines([]));
    } catch (err: any) {
      setEditStatus('error');
      setEditResult(err?.response?.data?.error || err.message || 'Failed to load order lines');
    }
  };

  // Load the thread's AI-prepared pending edit (if any) when the sub-thread
  // opens. Pending edits are thread-scoped, so we key on the thread ROOT id
  // (subject.thread_id ?? subject.id), not the customer. Failure / none leaves
  // pendingEdit null (no banner, normal manual flow) — non-fatal.
  const threadRootId = subject.thread_id ?? subject.id;

  // Persist the rep's MANUAL edits on the loaded existing order as a pending order
  // edit (edit_origin='manual'), so they survive close/reopen AND Sally respects
  // them (her staging idempotency guard backs off when a pending edit exists for
  // the SO). The editor computes the ops (diff vs the pristine SO lines) and calls
  // this debounced; we just persist. NON-FATAL — persistence must never block edits.
  const handlePersistManualEdit = useCallback(
    (ops: Array<{ op: string; item_id: number | null; shorthand: string | null; quantity: number | null }>) => {
      if (!editSoId) return;
      const tranid = editableOrders.find((o: any) => Number(o.id) === Number(editSoId))?.tranid;
      // Preserve Sally's staged HEADER change (set_header) so a manual LINE edit
      // doesn't wipe the ship-method/date change from the staged row (thread 4988).
      const pe = pendingEditRef.current;
      let headerOps: any[] = [];
      if (pe && Number(pe.netsuite_so_id) === Number(editSoId)) {
        try {
          const arr = Array.isArray(pe.proposed_edits)
            ? pe.proposed_edits
            : JSON.parse(pe.proposed_edits || '[]');
          headerOps = (arr || []).filter((o: any) => o && o.op === 'set_header');
        } catch (e) { headerOps = []; }
      }
      saveManualOrderEdit(threadRootId, {
        netsuite_so_id: editSoId,
        so_tranid: tranid ?? null,
        message_id: orderMsg?.id ?? null,
        customer_id: orderMsg?.customer_id ?? null,
        proposed_edits: [...ops, ...headerOps],
      }).catch((e) => console.error('Failed to persist manual order edit:', e));
    },
    [editSoId, threadRootId, editableOrders, orderMsg]
  );

  // Listen for the assist chat's force-correct broadcast while this sub-thread is
  // open; when it targets THIS thread, bump the repaint nonce so the SO-preview
  // re-fetches at once. Decoupled window-event, matching sl-focus-convo above.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (typeof d.threadRef === 'number' && d.threadRef === threadRootId) {
        setForceCorrectNonce(n => n + 1);
      }
    };
    window.addEventListener('sl-force-correct', handler);
    return () => window.removeEventListener('sl-force-correct', handler);
  }, [isOpen, threadRootId]);

  // 'sl-sally-working' — the chat / Prompt-AI announces when Sally starts and
  // stops working THIS thread so the card/modal can shimmer live (before the feed
  // poll would surface the backend ai_working flag). Filtered to this thread.
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (Number(d.threadRef) === Number(threadRootId)) setSallyWorkingEvt(!!d.working);
    };
    window.addEventListener('sl-sally-working', handler);
    return () => window.removeEventListener('sl-sally-working', handler);
  }, [threadRootId]);

  // Human-presence ping: tell the backend a human has this sub-thread EXPANDED
  // (viewing) so the AI stays silent, and RELEASE it on collapse/unmount so the
  // AI resumes. Keyed on isOpen + the thread ROOT id (subject.thread_id ??
  // subject.id) — the same id pending edits and SuggestedReply use, so the
  // signal targets the whole sub-thread, not an individual message.
  //   - open  -> setThreadViewing(rootId, true) now + a 60s heartbeat re-ping
  //              (keeps the backend's ~150s TTL fresh while it stays open).
  //   - close/unmount -> clear the heartbeat + setThreadViewing(rootId, false).
  //   - pagehide (tab closed/hidden while open) -> best-effort release.
  // All calls are non-fatal (setThreadViewing swallows failures); presence must
  // never block rendering or disrupt the pendingEdit / order-editor effects.
  const viewingHeartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    setThreadViewing(threadRootId, true);
    viewingHeartbeatRef.current = setInterval(() => {
      setThreadViewing(threadRootId, true);
    }, 60000);
    const onPageHide = () => { setThreadViewing(threadRootId, false); };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      if (viewingHeartbeatRef.current != null) {
        clearInterval(viewingHeartbeatRef.current);
        viewingHeartbeatRef.current = null;
      }
      setThreadViewing(threadRootId, false);
    };
  }, [isOpen, threadRootId]);

  useEffect(() => {
    if (!isOpen) { setPendingEdit(null); setPendingEdits([]); stagedRef.current = null; return; }
    let cancelled = false;
    getPendingOrderEdits(threadRootId)
      .then((rows) => {
        if (cancelled) return;
        // All still-pending rows (applied/discarded ignored) — one per SO; each is
        // a staged-edit preview tab (thread 4988). De-dupe to the newest row per SO.
        const pend = (rows || []).filter((r: any) => !r.status || r.status === 'pending');
        const bySo = new Map<number, any>();
        for (const r of pend) {
          const sid = Number(r.netsuite_so_id);
          if (sid && !bySo.has(sid)) bySo.set(sid, r);   // rows are newest-first
        }
        const uniq = Array.from(bySo.values());
        setPendingEdits(uniq);
        // Keep the rep's ACTIVE tab if its SO is still staged; else the newest.
        const active = uniq.find((r) => Number(r.netsuite_so_id) === Number(editSoIdRef.current))
          || uniq[0] || null;
        setPendingEdit(active);
      })
      .catch(() => { if (!cancelled) { setPendingEdit(null); setPendingEdits([]); } });
    return () => { cancelled = true; };
    // forceCorrectNonce/openTick: when Sally's chat force-corrects an EXISTING
    // order from side-by-side, she stages the change on THIS thread's pending edit
    // (ASSIST_FORCE_CORRECT_EDIT_TARGET_MODE). Refetch so the overlay below repaints
    // with her change immediately (event) and within ~2s (poll) — otherwise the
    // staged edit is written but the rep sees nothing until reload.
  }, [isOpen, threadRootId, forceCorrectNonce, openTick]);

  // Auto-stage: once a pending edit is loaded, AUTO-SELECT its SO via the EXISTING
  // handler so the editor mounts in edit mode pre-loaded with that SO's current
  // lines. Guarded by stagedRef so we select once per pending row. The overlay of
  // proposed_edits onto the loaded lines happens in the effect below (after the
  // lines arrive), so we don't reimplement the line fetch here.
  useEffect(() => {
    if (!pendingEdit || !pendingEdit.netsuite_so_id) return;
    if (stagedRef.current === pendingEdit.id) return;
    stagedRef.current = pendingEdit.id;
    handleSelectEditOrder(Number(pendingEdit.netsuite_so_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEdit]);

  // Hydrate the human-locked edit target (Part B): when the sub-thread opens and a
  // rep previously locked an existing SO in the dropdown (with NO pending edit yet),
  // restore that selection so it survives the reload. Runs once per open; skipped
  // when a pending edit already drove the selection (that path owns it) or the rep
  // has already picked something this session. persist=false (we're RESTORING, not
  // re-locking). NON-FATAL: a failure just leaves the dropdown at "New order".
  const editTargetHydratedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isOpen || !orderCustomerId) return;
    if (editableOrders.length === 0) return;      // need the orders to load lines
    if (pendingEdit || editSoId != null) return;  // already selected — don't override
    if (editTargetHydratedRef.current === threadRootId) return;
    editTargetHydratedRef.current = threadRootId;
    let cancelled = false;
    getThreadEditTarget(threadRootId)
      .then((t) => {
        const soId = t?.netsuite_so_id;
        if (!cancelled && soId && editSoId == null && !pendingEdit) {
          handleSelectEditOrder(Number(soId), false);
        }
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderCustomerId, editableOrders, threadRootId, pendingEdit]);

  // Overlay proposed_edits onto the loaded SO lines for DISPLAY. Lines from
  // getSalesOrderLines are {line, item_id, item_name, quantity, rate}; proposed
  // edits are {op, item_id, shorthand, quantity}. Matching: prefer item_id when
  // present, else match on shorthand against item_name (case-insensitive). Runs
  // once per staged row (overlaidRef) so a rep's later manual qty edits aren't
  // clobbered when this effect would otherwise re-fire on editLines changes.
  // Keyed on the staged row id + a signature of its proposed_edits (not just the
  // id): a plain id guard re-applied ONCE and never repainted when Sally added a
  // second op to the SAME row from chat (edit-existing force-correct). Including
  // the ops signature re-overlays when the STAGED ops change, while an unchanged
  // signature still skips — so a rep's own in-progress manual qty tweaks (which
  // change editLines but not the staged ops) are not clobbered.
  const overlaidRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingEdit || editSoId !== Number(pendingEdit.netsuite_so_id)) return;

    let ops: any[] = [];
    try {
      ops = Array.isArray(pendingEdit.proposed_edits)
        ? pendingEdit.proposed_edits
        : JSON.parse(pendingEdit.proposed_edits || '[]');
    } catch (e) {
      ops = [];
    }
    const sig = `${pendingEdit.id}:${JSON.stringify(ops)}`;
    if (overlaidRef.current === sig) return;
    // A staged CANCELLATION carries no line ops — there is nothing to overlay.
    // Leave the SO's current lines as-is so the human sees exactly what they're
    // closing; the red/amber cancel banner + Cancel order button drive the close.
    if (pendingIsCancel) { overlaidRef.current = sig; return; }
    // Overlay from the PRISTINE baseline (not the current, possibly-already-overlaid
    // editLines) so a re-apply on a staged-ops change is idempotent — never a
    // double-apply. Falls back to editLines only if the baseline hasn't loaded yet.
    const base = pristineEditLines || editLines;
    if (!base) return;
    overlaidRef.current = sig;
    if (!ops.length) { setEditLines([...base]); return; }

    const norm = (s?: string | null) => (s || '').trim().toLowerCase();
    const matchIdx = (lines: any[], op: any): number => {
      if (op.item_id != null) {
        const i = lines.findIndex((l) => l.item_id != null && Number(l.item_id) === Number(op.item_id));
        if (i >= 0) return i;
      }
      if (op.shorthand) {
        return lines.findIndex((l) => norm(l.item_name) === norm(op.shorthand));
      }
      return -1;
    };

    const next = [...base];
    for (const op of ops) {
      if (op.op === 'change_qty') {
        const i = matchIdx(next, op);
        if (i >= 0 && op.quantity != null) next[i] = { ...next[i], quantity: op.quantity };
        else if (op.quantity != null) {
          // No matching existing line — surface the proposed change as an added
          // line so the human still sees and can act on it.
          next.push({ line: null, item_id: op.item_id ?? null, item_name: op.shorthand || '', quantity: op.quantity, rate: null });
        }
      } else if (op.op === 'remove') {
        const i = matchIdx(next, op);
        if (i >= 0) next.splice(i, 1);
      } else if (op.op === 'add') {
        next.push({ line: null, item_id: op.item_id ?? null, item_name: op.shorthand || '', quantity: op.quantity ?? 1, rate: null });
      }
    }
    setEditLines(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEdit, editLines, editSoId, pristineEditLines]);

  // Discard the staged AI edit: mark it discarded server-side and clear the
  // editor back to the normal manual flow. Non-fatal if the resolve fails — the
  // editor still clears locally.
  const handleDiscardPending = async () => {
    const id = pendingEdit?.id;
    setPendingEdit(null);
    setPendingEdits([]);
    stagedRef.current = null;
    overlaidRef.current = null;
    handleSelectEditOrder(null, true);
    if (id) {
      try { await resolvePendingOrderEdit(id, 'discarded'); } catch (e) { /* non-fatal */ }
    }
  };

  // Switch the active staged-edit preview to another SO tab (thread 4988). Reuses
  // the single-editor machinery: point pendingEdit at that SO's row and reload its
  // pristine lines; the overlay effect then re-applies that SO's staged ops.
  const handleSelectStagedSo = (soId: number) => {
    if (Number(editSoId) === Number(soId)) return;
    const row = pendingEdits.find((r) => Number(r.netsuite_so_id) === Number(soId)) || null;
    overlaidRef.current = null;
    stagedRef.current = null;   // let the auto-stage effect re-select cleanly
    setPendingEdit(row);
    handleSelectEditOrder(Number(soId), false);
  };

  // "Reset to NetSuite": revert the ACTIVE staged preview to exactly what's in
  // NetSuite now (thread 4988). Discards the SO's staged line/header edits, re-
  // fetches the live order + header, and remounts the editor so no staged state
  // lingers. STAYS on the same SO (keeps the edit target — never flips to "New
  // order"), so a rep can redo the edit by hand without confusion. Non-fatal.
  const handleRevertToNetSuite = async () => {
    const soId = editSoId;
    if (!soId) return;
    if (!window.confirm(
      'Reset this preview to the current NetSuite order? Any staged changes for this order will be discarded.'
    )) return;
    const rootId = subject.thread_id ?? subject.id;
    const pe = pendingEdits.find((r) => Number(r.netsuite_so_id) === Number(soId)) || null;
    // Empty ops = discard the thread+SO pending row (AI or manual). Belt-and-braces:
    // also resolve the specific row id if we have it.
    try {
      await saveManualOrderEdit(rootId, {
        netsuite_so_id: Number(soId),
        so_tranid: pe?.so_tranid ?? null,
        proposed_edits: [],
      });
    } catch (e) { /* non-fatal */ }
    if (pe?.id) {
      try { await resolvePendingOrderEdit(pe.id, 'discarded'); } catch (e) { /* non-fatal */ }
    }
    // Drop the row locally + clear the overlay/auto-stage guards so nothing re-applies.
    setPendingEdits((prev) => prev.filter((r) => Number(r.netsuite_so_id) !== Number(soId)));
    setPendingEdit(null);
    overlaidRef.current = null;
    stagedRef.current = null;
    setEditRevertNonce((n) => n + 1);
    // Re-load the SO's LIVE lines + header for the SAME order (keeps edit target).
    await handleSelectEditOrder(Number(soId), false);
  };

  // Sally's staged HEADER change (ship method / ship date) for the ACTIVE SO, and
  // the seed the edit editor mounts with = the SO's current header + that override
  // (thread 4988). ship_method_name / ship_date_approx are display/flag helpers, so
  // they're kept OUT of the SoHeaderFields seed (which feeds the submit payload) and
  // surfaced separately for highlight.
  const activeSetHeaderOp = (pendingEdit && Number(pendingEdit.netsuite_so_id) === Number(editSoId))
    ? parseOps(pendingEdit).find((o: any) => o && o.op === 'set_header') || null
    : null;
  const activeHeaderOverride: any = (activeSetHeaderOp && activeSetHeaderOp.header) || null;
  const editHeaderChangedKeys: string[] = activeHeaderOverride
    ? Object.keys(activeHeaderOverride).filter((k) => k !== 'ship_method_name' && k !== 'ship_date_approx')
    : [];
  const editHeaderApproxDate = !!(activeHeaderOverride && activeHeaderOverride.ship_date_approx);
  const editHeaderSeed: SoHeaderSeed | undefined = (editHeaderBase || activeHeaderOverride)
    ? (() => {
        const merged: any = { ...(editHeaderBase || {}) };
        if (activeHeaderOverride) {
          for (const k of Object.keys(activeHeaderOverride)) {
            if (k === 'ship_method_name' || k === 'ship_date_approx') continue;
            merged[k] = activeHeaderOverride[k];
          }
        }
        return merged;
      })()
    : undefined;

  // Submit edited lines to an existing SO. Mirrors handleCreateSO's surfacing:
  // 409 (locked) -> tell the rep to create a new order instead; 400 (empty) and
  // other errors surface the server message.
  const handleSubmitChanges = async (soId: number, editedItems?: any[], hotLoad?: boolean, header?: any, weightLb?: number) => {
    setEditStatus('loading');
    setEditResult('');
    try {
      // Pass RAW items (not pre-mapped) so DISCOUNT/MARKETING credit lines keep
      // their line_kind + credit_amount — api.submitOrderChanges maps them to
      // negative-rate credit lines. Normal lines still auto-price.
      const items = (editedItems || []).filter((i: any) => i.item_id);
      if (items.length === 0) {
        setEditStatus('error');
        setEditResult('No resolved items to submit');
        return;
      }
      const result = await submitOrderChanges(
        soId,
        items,
        'Edited via SalesLogix — thread #' + (subject.thread_id ?? subject.id),
        hotLoad,
        header,
        weightLb,
        orderMsg?.id,
        activeSoGroup,
      );
      setEditStatus('success');
      setEditResult('Order updated' + (result?.status ? ' (' + result.status + ')' : '')
        + (result?.thread_autocompleted ? ' — thread completed' : ''));
      // If this submit applied an AI-prepared staged edit, mark it applied so the
      // backend stops re-offering it. Non-fatal: the PATCH already succeeded.
      if (pendingEdit?.id) {
        const stagedId = pendingEdit.id;
        setPendingEdit(null);
        try { await resolvePendingOrderEdit(stagedId, 'applied'); } catch (e) { /* non-fatal */ }
      }
      // The backend may have auto-completed the thread (same rule as a create);
      // refresh so the feed drops it from New.
      if (result?.thread_autocompleted) onAfterSend();
    } catch (err: any) {
      const status = err?.response?.status;
      setEditStatus('error');
      if (status === 409) {
        setEditResult('That order is locked and can no longer be edited — create a new order instead.');
      } else if (status === 400) {
        setEditResult(err?.response?.data?.error || 'An order edit must include at least one item.');
      } else {
        setEditResult(err?.response?.data?.error || err.message || 'Failed to submit changes');
      }
    }
  };

  // Cancel (close) the loaded existing SO. This is the ONLY cancel write and is
  // always HUMAN-clicked (from OrderItemEditor's confirm). On success: if a
  // staged cancellation drove this, resolve it 'applied'; clear the editor back
  // to the manual flow and mark the sub-thread COMPLETED. 409 (no longer
  // editable) surfaces the rep-facing message; other errors surface the body.
  const handleCancelOrder = async (soId: number) => {
    setEditStatus('loading');
    setEditResult('');
    try {
      const result = await cancelSalesOrder(soId);
      setEditStatus('success');
      setEditResult('Order cancelled' + (result?.status ? ' (' + result.status + ')' : ''));
      // If a staged cancellation drove this, mark it applied so the backend
      // stops re-offering it. Non-fatal: the close already succeeded.
      if (pendingEdit?.id) {
        const stagedId = pendingEdit.id;
        setPendingEdit(null);
        try { await resolvePendingOrderEdit(stagedId, 'applied'); } catch (e) { /* non-fatal */ }
      }
      onStatusChange(subject.id, 'COMPLETED');
    } catch (err: any) {
      const status = err?.response?.status;
      setEditStatus('error');
      if (status === 409) {
        setEditResult('That order can no longer be cancelled.');
      } else {
        setEditResult(err?.response?.data?.error || err.message || 'Failed to cancel order');
      }
    }
  };

  // referenced_order: Dev-3 surfaces this on the order message's parse when the
  // customer's text points at an existing SO (e.g. "add to my last order").
  // Read off a narrowed view since the Message type doesn't declare it yet.
  const referencedOrder = orderMsg
    ? (orderMsg as Message & { referenced_order?: string | null }).referenced_order || null
    : null;

  const actionBtn = 'text-sm text-gray-500 hover:text-brand-700 flex items-center py-1.5 -my-0.5';

  // The thread HEADER block (subject row + action row). Rendered in the collapsed
  // card AND, identically, as the modal header — so the modal header matches the
  // thread. `onDetails` is the open handler in the card and the modal's animated
  // close inside the modal. The Details/Comments view buttons key off `isOpen` +
  // `modalView` (closure), not a param.
  const renderHeaderBlock = (onDetails: () => void) => (
    <>
      <div className="px-3 py-2 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 cursor-pointer" onClick={onDetails}>
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span className={`px-1.5 py-0.5 text-xs font-semibold rounded-full ${intentBadgeStyles[intent] || intentBadgeStyles['UNKNOWN']}`}>
              {intent}
            </span>
            {subject.needs_human_review && (
              <span className="flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                Needs Review
              </span>
            )}
            {/* Live "who's working this" tags — pair with the card/modal shimmer. */}
            {sallyWorking && (
              <span title="Sally is working on this thread right now"
                className="inline-flex items-center gap-1 flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                Sally working
              </span>
            )}
            {!sallyWorking && otherWorker && (
              <span title={`${otherWorker} is in this thread`}
                className="inline-flex items-center gap-1 flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                {otherWorker}
              </span>
            )}
            {/* VOICEMAIL: an unreturned missed call. On the sub-thread header
                (not just the card header) because one conversation can hold
                several sub-threads, and a rep needs to see WHICH one nobody
                answered. Derived from the id prefix, so it shows immediately —
                it does not wait for the transcript detail to load. */}
            {isVoicemailThread && (
              <span
                title="A voicemail — nobody answered this call. It needs a callback."
                className="inline-flex items-center gap-1 flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-amber-100 text-amber-800"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 16a3.5 3.5 0 100-7 3.5 3.5 0 000 7zm11 0a3.5 3.5 0 100-7 3.5 3.5 0 000 7zm-11 0h11" />
                </svg>
                Voicemail
              </span>
            )}
            {/* "Sally needs help": computed live server-side (auto-clears). Opening
                the thread posts her question/issue to the Comments log (not the
                chat). Purple + a small spark to read as "the AI is asking". */}
            {subject.sally_needs_help && (
              <span
                title={subject.sally_help_reason
                  ? `Sally: ${subject.sally_help_reason}`
                  : 'Sally needs a hand with this thread'}
                className="inline-flex items-center gap-1 flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-purple-100 text-purple-800"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l1.9 4.8L18.7 8l-3.8 3.1L16 16l-4-2.6L8 16l1.1-4.9L5.3 8l4.8-1.2z" />
                </svg>
                Sally needs help
              </span>
            )}
            {/* "Awaiting reply": the latest message is inbound and unanswered — the
                thread hasn't been responded to yet. Blue (distinct from amber
                Needs-Review / green ready); the pulsing dot draws the eye. */}
            {awaitingReply && (
              <span
                title="The customer's latest message hasn't been replied to yet"
                className="inline-flex items-center gap-1 flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                Awaiting reply
              </span>
            )}
            <span className="text-xs text-gray-400 whitespace-nowrap">{messages.length} msg</span>
          </div>
          <p className="text-sm text-gray-800 truncate">{subject.raw_message}</p>
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {fmtTime(subject.received_at)} <span className="text-gray-400">#{subject.thread_id ?? subject.id}</span>
          </span>
          {/* CALL thread metadata (from the hydrated transcript detail): rep,
              direction, duration, number. Present once the detail loads. */}
          {isCallThread && callDetail && (
            <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500">
              {/* A voicemail has no rep on the call (nobody answered) and no
                  meaningful direction to state — "Voicemail" says both. Its
                  duration is the length of the MESSAGE, so it is labeled
                  rather than left as a bare m:ss that reads like talk time. */}
              {callDetail.is_voicemail ? (
                <span className="font-medium text-amber-700">Voicemail — missed call</span>
              ) : (
                <>
                  {callDetail.rep_name && <span>Rep: {callDetail.rep_name}</span>}
                  {callDetail.direction && <span className="capitalize">{callDetail.direction} call</span>}
                </>
              )}
              {fmtCallDuration(callDetail.duration_seconds) && (
                <span>{callDetail.is_voicemail
                  ? `${fmtCallDuration(callDetail.duration_seconds)} message`
                  : fmtCallDuration(callDetail.duration_seconds)}</span>
              )}
              {fmtCallNumber(callDetail.external_number) && <span>{fmtCallNumber(callDetail.external_number)}</span>}
            </div>
          )}
          {/* Employee-originated thread (#7): rep texting on behalf of a customer.
              Show an employee badge + the target-customer confirm dropdown. The
              dropdown is auto-filled with the AI's determination (the resolved
              target) and is always editable for human override. Stop click
              propagation so interacting with it doesn't toggle the sub-thread. */}
          {isEmployeeThread && (
            <div className="mt-1.5 flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
              <span
                className="flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800"
                title={employeeRepName ? `Sent by ${employeeRepName} on behalf of the customer` : 'Employee-originated thread'}
              >
                {employeeBadgeText}
              </span>
              {targetUnresolved && !targetValue && (
                <span className="flex-shrink-0 whitespace-nowrap text-xs font-medium text-amber-700">
                  Customer needed
                </span>
              )}
              <CustomerConfirmSelect
                value={targetValue}
                candidates={targetCandidates}
                onSelect={handleConfirmCustomer}
                placeholder={targetUnresolved ? 'Choose customer…' : 'Confirm customer…'}
                className="text-sm border border-gray-300 rounded px-2 py-1 text-gray-700 w-full sm:w-52 focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          )}
        </div>
        <select
          data-thread-status
          value={subject.status}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onStatusChange(subject.id, e.target.value)}
          className={`text-sm rounded px-2 py-1 flex-shrink-0 focus:outline-none focus:ring-2 ${
            escalated
              ? 'bg-red-100 text-red-800 font-bold border-2 border-red-500 ring-2 ring-red-400 focus:ring-red-500'
              : 'border border-gray-300 focus:ring-brand-500'
          }`}
        >
          <option value="NEW">New</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="ESCALATED">Escalated</option>
        </select>
      </div>

      <div className="px-3 pb-2 pt-2 border-t border-gray-100 flex items-center gap-x-4 gap-y-1 flex-wrap">
        {/* Details / Comments: when the modal is open these switch which view it
            shows (the order/conversation DETAILS vs the thread's COMMENT history);
            from the collapsed card they open the modal to that view. The active
            view is highlighted while the modal is open. */}
        <button
          onClick={() => openTo('details')}
          className={isOpen && modalView === 'details'
            ? 'text-sm text-brand-700 font-semibold flex items-center py-1.5 -my-0.5'
            : actionBtn}>
          Details
        </button>
        <button
          onClick={() => openTo('comments')}
          className={isOpen && modalView === 'comments'
            ? 'text-sm text-brand-700 font-semibold flex items-center py-1.5 -my-0.5'
            : actionBtn}>
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
          Comments
          <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] px-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
            {subject.comment_count ?? 0}
          </span>
        </button>
        {/* Flag / clear human-review (any role). Toggles needs_human_review then
            refreshes so the badge + SignalR push propagate. */}
        <button
          onClick={() => handleFlagReview(!subject.needs_human_review)}
          disabled={flagBusy}
          className={`${actionBtn} disabled:opacity-50`}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 2H21l-3 6 3 6h-8.5l-1-2H5a2 2 0 00-2 2zm9-13.5V9" />
          </svg>
          {subject.needs_human_review ? 'Clear review' : 'Flag for review'}
        </button>
        {/* "Prompt AI": on a TEXT thread the AI reads the thread and drafts the
            next pending question (bypassing human-presence suppression — the rep
            explicitly asked). On a CALL thread it works the ORDER STATE only
            (resolves items / completes the SO preview) and NEVER drafts a message —
            you can't text a call. Hidden once the thread is completed. */}
        {subject.status !== 'COMPLETED' && (
          <button
            onClick={(e) => { e.stopPropagation(); handleAdvance(); }}
            disabled={advanceBusy}
            title={isCallThread
              ? "Have the AI work this call's order state (resolve items; no message is drafted)"
              : "Have the AI read this thread and draft the next step"}
            className={`${actionBtn} disabled:opacity-50`}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            {advanceBusy ? 'Prompting…' : 'Prompt Sally'}
          </button>
        )}
      </div>
    </>
  );

  // Working-shimmer signals (#5 + "someone else is here"). Sally shimmer wins over
  // the human one when both apply. otherWorker excludes the current user (their own
  // presence ping shouldn't shimmer their own view).
  const _curUser = (getCurrentUser()?.name || '').trim().toLowerCase();
  const _workingBy = (subject as any).working_by as string | undefined;
  const otherWorker = (_workingBy && _workingBy.trim().toLowerCase() !== _curUser)
    ? _workingBy : null;
  const sallyWorking = sallyWorkingEvt || !!(subject as any).ai_working;
  // Collapsed card/header: Sally OR another teammate. Open modal: SALLY ONLY — the
  // "someone else is here" shimmer is only useful at-a-glance in the feed; inside
  // the modal you're already working it, so a teammate shimmer there is just noise
  // (Michael). The teammate TAG still shows in the header for awareness.
  const cardWorkingClass = sallyWorking ? 'sl-working-sally'
    : (otherWorker ? 'sl-working-human' : '');
  const modalWorkingClass = sallyWorking ? 'sl-working-sally' : '';

  return (
    <div ref={rootRef} className={"border border-gray-200 rounded-lg bg-white overflow-hidden " + (!isOpen ? cardWorkingClass : '')}>
      {renderHeaderBlock(openFromCard)}

      {/* The open thread's detail body renders in a MODAL OVERLAY (portal on
          document.body), not inline in the feed list — so a background poll/
          re-sort of the list underneath can't reflow, move, or close it. onToggle
          closes the modal (clears the lifted openThreadId). All of this
          sub-thread's state/effects (presence heartbeat, order editor, pending
          edits) are preserved: SubThread stays mounted and keyed by its thread id,
          only its detail body's DOM location moves into the portal. */}
      {isOpen && (
        <ThreadModal
          onClose={onToggle}
          originRect={originRect}
          threadRef={subject.thread_id ?? subject.id}
          workingClass={modalWorkingClass}
          renderHeader={(close) => renderHeaderBlock(close)}
        >
        {/* DETAILS view (order preview + conversation). Kept MOUNTED but hidden
            when the Comments view is active, so switching views never unmounts the
            order editor and loses unsaved edits. */}
        <div className={'space-y-2 ' + (modalView === 'details' ? '' : 'hidden')}>
          {/* Quick-link "Jump to" bar — ALWAYS visible while scrolling; lists ONLY
              the sections that exist on this thread, and scrolls each to the top. */}
          {modalView === 'details' && detailSections.length > 0 && (
            <div className="sticky top-0 z-10 -mx-3 px-3 py-1.5 bg-gray-50/95 backdrop-blur border-b border-gray-200 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-gray-400 mr-0.5">Jump to:</span>
              {detailSections.map(s => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => scrollToSection(s.ref)}
                  className="text-xs px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-600 hover:text-brand-700 hover:border-brand-300"
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
          {/* Generated messages (Sally's drafted reply / open question). Text-thread
              only (hidden on CALL threads). The components self-hide when empty and
              report content via onHasContent, so the collapse header + quick-link only
              appear when a generated message exists. Kept MOUNTED (hidden via CSS when
              collapsed) so they don't refetch on expand. */}
          {!isCallThread && (
            <div ref={genRef} className="scroll-mt-16">
              {generatedExists && sectionHeader('generated', 'Generated messages')}
              <div className={collapsed['generated'] ? 'hidden' : 'space-y-2'}>
                <SuggestedReply threadId={subject.thread_id ?? subject.id} refreshKey={`${threadRefreshKey}:${advanceNonce}:${openTick}`} onAfterAction={onAfterSend} onHasContent={setHasSuggested} />
                <OpenQuestion threadId={subject.thread_id ?? subject.id} refreshKey={`${threadRefreshKey}:${advanceNonce}:${openTick}`} onAfterAction={onAfterSend} onHasContent={setHasOpenQuestion} />
              </div>
            </div>
          )}
          {/* + Add preview: manually stage another Order / RMA / Credit Memo on this
              thread — mounts the container if the AI didn't auto-detect it, and
              selects the new tab. The human's counterpart to Sally's auto-split.
              Shown on CALL threads too — an order/RMA/CM can be placed over the phone,
              and the rep needs to stage one from the transcript just the same. */}
          <div className="relative">
              <button
                type="button"
                onClick={() => setAddMenuOpen(o => !o)}
                onBlur={() => setTimeout(() => setAddMenuOpen(false), 150)}
                className="text-sm text-gray-500 hover:text-brand-700 flex items-center gap-1 py-1"
              >
                + Add preview
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {addMenuOpen && (
                <div className="absolute z-20 mt-1 w-44 bg-white border border-gray-200 rounded shadow-lg">
                  {([['SO', 'Order'], ['RMA', 'RMA'], ['CM', 'Credit Memo']] as [PreviewType, string][]).map(([t, lbl]) => (
                    <button
                      key={t}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); setAddMenuOpen(false); handleAddPreview(t); }}
                      className="block w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-brand-50"
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              )}
            </div>
          {orderMsg && (
            <div ref={orderSecRef} className="scroll-mt-16">
              {sectionHeader('order', 'Order preview')}
              <div className={collapsed['order'] ? 'hidden' : 'space-y-2'}>
              {referencedOrder && (
                <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                  References {referencedOrder}
                </div>
              )}
              {/* Order-preview tabs (one per staged order) + this tab's customer. */}
              <PreviewTabs
                label="Order"
                groups={soGroups.length ? soGroups : [1]}
                active={activeSoGroup}
                onSelect={setActiveSoGroup}
                onRemove={(g) => handleRemovePreview('SO', g)}
              />
              {previewCustomerRow('SO', activeSoGroup)}
              {/* Edit an existing order: pick one of the customer's editable SOs.
                  While an order is selected we mount the editor in 'edit' mode
                  (prefilled with that SO's lines); clearing it restores the
                  normal new-order create path below. */}
              {editableOrders.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-xs text-gray-500">Edit existing order:</label>
                  {/* Custom dropdown (not a native <select>) so each order row can
                      show a hover preview of its line items / quantities. */}
                  <EditableOrderSelect
                    orders={editableOrders}
                    value={editSoId ?? null}
                    onChange={(id) => handleSelectEditOrder(id, true)}
                    className="flex items-center text-sm border border-gray-300 rounded px-2 py-1 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 max-w-full sm:max-w-[18rem]"
                  />
                </div>
              )}
              {/* AI-prepared edit banner: shown when a staged pending edit is
                  driving the currently-selected SO, so the human knows the AI
                  pre-loaded these changes and just needs to review + Submit. */}
              {pendingEdit && editSoId === Number(pendingEdit.netsuite_so_id) && !pendingIsCancel && (
                <div className="flex items-start justify-between gap-2 text-xs text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5">
                  <div className="flex items-start gap-1.5 min-w-0">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span>
                      {pendingEdit.edit_origin === 'manual'
                        ? `Your staged changes to ${pendingEdit.so_tranid || ('SO ' + pendingEdit.netsuite_so_id)} — review and Submit changes to apply, or Discard to revert`
                        : `AI-prepared edit to ${pendingEdit.so_tranid || ('SO ' + pendingEdit.netsuite_so_id)} — review and Submit changes to apply`}
                    </span>
                  </div>
                  <button
                    onClick={handleDiscardPending}
                    className="flex-shrink-0 text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap"
                  >
                    Discard
                  </button>
                </div>
              )}
              {/* AI-prepared CANCELLATION banner: distinct red/amber (vs the
                  indigo edit banner). The SO is auto-loaded so the human sees
                  what they're closing; there are NO line changes to apply. The
                  human closes it via the Cancel order button in the editor. */}
              {pendingEdit && editSoId === Number(pendingEdit.netsuite_so_id) && pendingIsCancel && (
                <div className="flex items-start justify-between gap-2 text-xs text-red-800 bg-red-50 border border-red-300 rounded px-2 py-1.5">
                  <div className="flex items-start gap-1.5 min-w-0">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <span>
                      AI-prepared CANCELLATION — review and close order {pendingEdit.so_tranid || ('SO ' + pendingEdit.netsuite_so_id)}
                    </span>
                  </div>
                  <button
                    onClick={handleDiscardPending}
                    className="flex-shrink-0 text-red-600 hover:text-red-800 font-medium whitespace-nowrap"
                  >
                    Discard
                  </button>
                </div>
              )}
              {editSoId && editLines ? (
                <>
                  {/* Staged-edit preview TABS (thread 4988): when one message staged
                      edits against 2+ SOs, show a tab per SO so the rep can review
                      each preview. Only shown when there's more than one. */}
                  {(() => {
                    const tabs: { id: number; label: string }[] = [];
                    const seen = new Set<number>();
                    for (const r of pendingEdits) {
                      const sid = Number(r.netsuite_so_id);
                      if (sid && !seen.has(sid)) {
                        seen.add(sid);
                        tabs.push({ id: sid, label: r.so_tranid || ('SO ' + sid) });
                      }
                    }
                    if (tabs.length < 2) return null;
                    return (
                      <div className="flex items-center gap-1 flex-wrap mb-2">
                        <span className="text-xs text-gray-500 mr-1">Staged edits:</span>
                        {tabs.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => handleSelectStagedSo(t.id)}
                            className={
                              'text-xs px-2 py-1 rounded border ' +
                              (Number(editSoId) === t.id
                                ? 'bg-brand-600 text-white border-brand-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                            }
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {/* Reset the whole preview back to what's in NetSuite right now
                      (thread 4988): discards Sally's staged line/header changes for
                      this SO and reloads the live order, so a rep can start clean and
                      edit by hand. Stays on this order (does not switch to New order). */}
                  <div className="flex justify-end mb-1">
                    <button
                      onClick={handleRevertToNetSuite}
                      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded px-2 py-1 hover:bg-gray-50"
                      title="Discard staged changes and reload this order exactly as it is in NetSuite"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Reset to NetSuite
                    </button>
                  </div>
                  {/* Compare the CURRENT NetSuite order to what it becomes once the
                      staged/edited changes are submitted (thread 4962 ask): so a rep
                      never needs a second window to see the before/after. */}
                  {pristineEditLines && (
                    <div className="mb-2">
                      <OrderDiffPanel
                        soLabel={
                          editableOrders.find((o: any) => Number(o.id) === Number(editSoId))?.tranid
                          || (pendingEdit && Number(pendingEdit.netsuite_so_id) === editSoId
                                ? pendingEdit.so_tranid : null)
                          || ('SO ' + editSoId)
                        }
                        before={nsLinesToDiff(pristineEditLines)}
                        after={liveEditItems ? editableItemsToDiff(liveEditItems) : nsLinesToDiff(editLines)}
                      />
                    </div>
                  )}
                  <OrderItemEditor
                    key={'edit-' + editSoId + '-r' + editRevertNonce}
                    messageId={orderMsg.id}
                    customerId={custFor('SO', activeSoGroup).id ?? orderMsg.customer_id}
                    items={editLines}
                    refreshKey={editSoId}
                    mode="edit"
                    initialHotLoad={threadHotLoad}
                    isEmployeeThread={isEmployeeThread || !!employeeRepName}
                    employeeName={employeeRepName}
                    onCreateSO={(items, hotLoad, header, weightLb) => handleCreateSO(activeSoGroup, items, hotLoad, header, weightLb)}
                    onSubmitChanges={(items, hotLoad, header, weightLb) => handleSubmitChanges(editSoId, items, hotLoad, header, weightLb)}
                    onCancelOrder={() => handleCancelOrder(editSoId)}
                    editBaseline={pristineEditLines || undefined}
                    onPersistEdit={handlePersistManualEdit}
                    onItemsChange={setLiveEditItems}
                    editHeaderSeed={editHeaderSeed}
                    editHeaderChangedKeys={editHeaderChangedKeys}
                    editHeaderApproxDate={editHeaderApproxDate}
                    promoLines={editPromoLines}
                    soStatus={editStatus}
                    soResult={editResult}
                  />
                </>
              ) : (
                <OrderItemEditor
                  /* Remount when the active tab changes so each order preview
                     loads its own group's lines cleanly. */
                  key={'so-' + orderMsg.id + '-g' + activeSoGroup}
                  messageId={orderMsg.id}
                  customerId={custFor('SO', activeSoGroup).id ?? orderMsg.customer_id}
                  group={activeSoGroup}
                  /* Only the primary tab seeds from the messages-poll inline items;
                     additional tabs fetch their group's lines (initialItems would be
                     ungrouped and briefly show the wrong lines). */
                  items={activeSoGroup === 1 ? orderMsg.items : undefined}
                  /* Compose advanceNonce so a "Prompt AI" press always refetches
                     the SO preview in place — even when the AI only drafts the
                     next question and resolves no line (so updated_at is NOT
                     bumped). Mirrors the SuggestedReply/OpenQuestion refreshKey
                     above; OrderItemEditor's dirtyRef guards unsaved rep edits. */
                  /* forceCorrectNonce bumps when the AI Assist chat force-corrects
                     THIS thread's staged order (R2.6 #8), forcing an immediate
                     preview re-fetch instead of waiting on the openTick poll. */
                  refreshKey={`${threadRefreshKey}:${advanceNonce}:${openTick}:${forceCorrectNonce}`}
                  initialHotLoad={threadHotLoad}
                  isEmployeeThread={isEmployeeThread || !!employeeRepName}
                  employeeName={employeeRepName}
                  onCreateSO={(items, hotLoad, header, weightLb) => handleCreateSO(activeSoGroup, items, hotLoad, header, weightLb)}
                  soStatus={soStatusByGroup[activeSoGroup] || 'idle'}
                  soResult={soResultByGroup[activeSoGroup] || ''}
                />
              )}
              {editSoId && !editLines && editStatus !== 'error' && (
                <div className="text-xs text-gray-500">Loading order lines…</div>
              )}
              {editSoId && editStatus === 'error' && !editLines && (
                <div className="text-xs text-red-600">{editResult}</div>
              )}
              </div>
            </div>
          )}
          {/* RMA (returns) preview — mounts when the thread carries a return
              request. Same editor as the SO preview in 'rma' mode: every field
              (item, qty, unit, return price, reason) is editable and STICKY, plus
              a "Create RMA" action that creates a write-gated Return Authorization.
              Rendered independently of orderMsg so a thread could show both. */}
          {rmaMsg && (
            <div ref={rmaSecRef} className="scroll-mt-16">
              {sectionHeader('rma', 'RMA preview')}
              <div className={collapsed['rma'] ? 'hidden' : 'space-y-2'}>
              {referencedOrder && (
                <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                  References {referencedOrder}
                </div>
              )}
              {/* One tab per RMA group (a thread can require several Return
                  Authorizations). The active editor renders only its group's lines,
                  with its own reason + Create RMA; the per-line "RMA #" picker moves
                  lines between groups. Add/remove RMAs via the tabs + "+ Add preview". */}
              <PreviewTabs
                label="RMA"
                groups={rmaGroups.length ? rmaGroups : [1]}
                active={activeRmaGroup}
                onSelect={setActiveRmaGroup}
                onRemove={(g) => handleRemovePreview('RMA', g)}
              />
              {previewCustomerRow('RMA', activeRmaGroup)}
              <OrderItemEditor
                key={'rma-' + rmaMsg.id + '-g' + activeRmaGroup}
                messageId={rmaMsg.id}
                customerId={custFor('RMA', activeRmaGroup).id ?? rmaMsg.customer_id}
                items={undefined}
                mode="rma"
                rmaGroup={activeRmaGroup}
                rmaGroups={rmaGroups.length ? rmaGroups : [1]}
                onMoveLine={handleMoveRmaLine}
                refreshKey={`${threadRefreshKey}:${advanceNonce}:${openTick}:${forceCorrectNonce}`}
                onCreateSO={(items, hotLoad, header, weightLb) => handleCreateSO(activeSoGroup, items, hotLoad, header, weightLb)}
                onCreateRMA={(items) => handleCreateRMA(activeRmaGroup, items)}
                soStatus={rmaStatusByGroup[activeRmaGroup] || 'idle'}
                soResult={rmaResultByGroup[activeRmaGroup] || ''}
              />
              </div>
            </div>
          )}
          {/* Credit Memo (CM) preview — mounts on a CREDIT thread (durable
              is_credit_request flag, or a started CM). Kelsey ties in an invoice
              (reference-only), adds DIS-item credit line(s) at a value, and creates
              a standalone NetSuite Credit Memo. HARD customer guard (no customer ->
              no create). Rendered independently of orderMsg/rmaMsg so a thread could
              show more than one preview. */}
          {creditMsg && (
            <div ref={cmSecRef} className="scroll-mt-16">
              {sectionHeader('cm', 'Credit memo')}
              <div className={collapsed['cm'] ? 'hidden' : 'space-y-2'}>
              {/* One tab per credit memo (a thread can carry several). Active editor
                  renders its group's credit lines; add/remove via tabs + "+ Add preview". */}
              <PreviewTabs
                label="CM"
                groups={cmGroups.length ? cmGroups : [1]}
                active={activeCmGroup}
                onSelect={setActiveCmGroup}
                onRemove={(g) => handleRemovePreview('CM', g)}
              />
              {previewCustomerRow('CM', activeCmGroup)}
              <CreditMemoEditor
                key={'cm-' + creditMsg.id + '-g' + activeCmGroup}
                messageId={creditMsg.id}
                group={activeCmGroup}
                customerId={custFor('CM', activeCmGroup).id ?? creditMsg.customer_id}
                customerName={custFor('CM', activeCmGroup).name ?? creditMsg.customer_name}
                refreshKey={`${threadRefreshKey}:${advanceNonce}:${openTick}:${forceCorrectNonce}`}
                cmStatus={cmStatusByGroup[activeCmGroup] || 'idle'}
                cmResult={cmResultByGroup[activeCmGroup] || ''}
                onCreateCM={(items, header, memo) => handleCreateCM(activeCmGroup, items, header, memo)}
              />
              </div>
            </div>
          )}
          {/* CALL-thread hydration states: spinner while the transcript detail
              loads, an error box on failure, and a quiet note when the call has
              no segments yet (transcription still pending/partial). */}
          {isCallThread && callLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-700"></div>
            </div>
          )}
          {isCallThread && !callLoading && callError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
              {callError}
            </div>
          )}
          {isCallThread && !callLoading && !callError && callDetail && callMessages.length === 0 && (
            <div className="text-sm text-gray-500 py-4 text-center">
              {callDetail.is_voicemail
                ? 'No transcript for this voicemail — listen to the recording in 8x8 and call back.'
                : `No transcript yet (status: ${callDetail.transcript_status}).`}
            </div>
          )}

          {/* Conversation section — the message bubbles + the reply control,
              collapsible like the previews. */}
          <div ref={convoSecRef} className="scroll-mt-16">
          {conversationExists && sectionHeader('conversation', 'Conversation')}
          <div className={collapsed['conversation'] ? 'hidden' : 'space-y-2'}>
          {/* The bubble map, GROUPED INTO SPEAKER TURNS. CALL threads render
              their synthesized transcript segments THROUGH the same MessageCard
              as SMS/email — identical bubbles by construction. Each turn is a
              run of consecutive same-speaker messages: the sender/time header
              shows once (on the first bubble), the rest are `grouped` and stack
              tightly, with a wider gap BETWEEN turns — so a long back-and-forth
              reads as clean turn-taking, one speaker then the next. */}
          {buildTurns(isCallThread ? callMessages : asc).map((turn, ti) => (
            <div key={turn[0].id} className={`flex flex-col gap-0.5 ${ti > 0 ? 'mt-2.5' : ''}`}>
              {turn.map((m, mi) => (
                <div
                  key={m.id}
                  className={`flex items-center gap-1 group ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="w-fit max-w-[85%] sm:max-w-[36rem]">
                    <MessageCard message={m} grouped={mi > 0} />
                  </div>
                  {/* Human override: move a mis-threaded INBOUND message to the
                      right thread. Sits to the SIDE of the bubble, revealed on
                      hover. Never on CALL threads — segments aren't movable. */}
                  {m.direction !== 'outbound' && onReassign && !isCallThread && (
                    <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <MoveMessageControl
                        messageId={m.id}
                        otherThreads={otherThreads}
                        onReassign={onReassign}
                        onSplitToNewThread={onSplitToNewThread}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          {/* Reply lives at the bottom-right of the message list, like a text
              app. Hidden entirely on CALL threads — you can't text a call. */}
          {!isCallThread && (showReply ? (
            <CustomerReply
              messageId={subject.id}
              /* Only rendered when !isCallThread, so the channel here is
                 SMS/EMAIL; narrow explicitly for CustomerReply's prop type. */
              channel={subject.channel === 'EMAIL' ? 'EMAIL' : 'SMS'}
              recipient={contact}
              onClose={() => setShowReply(false)}
              onSent={() => onAfterSend()}
            />
          ) : (
            <div className="flex justify-end items-center gap-2 pt-1">
              <button
                onClick={() => setShowReply(true)}
                className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-brand-700 rounded-full hover:bg-brand-800"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Reply
              </button>
            </div>
          ))}
          </div>
          </div>
        </div>
        {/* COMMENTS view (default) — the thread's running history, incl. Sally's
            log entries. Kept mounted alongside Details; only one is visible. */}
        <div className={modalView === 'comments' ? '' : 'hidden'}>
          <MessageComments messageId={subject.id} />
        </div>
        </ThreadModal>
      )}
    </div>
  );
};

const ConversationCard: React.FC<ConversationCardProps> = ({ messages, onStatusChange, onAfterSend, openThreadId, onOpenThread, showCompleted = false }) => {
  const [expanded, setExpanded] = useState(false);
  // Which view the OPEN sub-thread's modal shows (DETAILS vs COMMENT history).
  // Held here, not in SubThread, because opening a thread on an employee
  // conversation moves it into the stable slot and remounts SubThread — a local
  // state would reset to 'details', so clicking Comments from the collapsed card
  // opened Details. Only one thread is open per feed, so a single value suffices.
  const [modalView, setModalView] = useState<'comments' | 'details'>('details');
  // Which customer groups (employee conversations only) are expanded. Default: all
  // collapsed, so an employee with several customers is easy to scan — click a
  // customer header to reveal that customer's info + threads. Keyed by group key.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  // The open sub-thread is driven by the LIFTED openThreadId (MessageFeed owns it),
  // so exactly one thread is open across the whole feed and it renders in a modal
  // overlay rather than expanding inline. Opening a thread here just sets that
  // shared id; opening one thus closes any other (feed-wide, not per-card).

  // A notification click (focusConversation) targets this card by sender id;
  // expand the conversation, open the specific sub-thread (via the lifted state),
  // and scroll the card into view.
  const rootRef = useRef<HTMLDivElement>(null);
  const senderId = messages[0]?.sender_id;
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (d.senderId !== senderId) return;
      setExpanded(true);
      if (typeof d.threadKey === 'number') onOpenThread(d.threadKey);
      requestAnimationFrame(() =>
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      );
    };
    window.addEventListener('sl-focus-convo', handler);
    return () => window.removeEventListener('sl-focus-convo', handler);
  }, [senderId, onOpenThread]);

  const threadMap = new Map<number, Message[]>();
  for (const m of messages) {
    const key = (m.thread_id ?? m.id) as number;
    if (!threadMap.has(key)) threadMap.set(key, []);
    threadMap.get(key)!.push(m);
  }
  const subjectOf = (msgs: Message[]): Message => {
    const a = [...msgs].sort((x, y) => msgTime(x) - msgTime(y) || (x.id - y.id));
    return a.find(m => (m.thread_id ?? m.id) === m.id) || a[0];
  };

  const subThreads = Array.from(threadMap.entries())
    .map(([key, msgs]) => ({ key, msgs }))
    // Completed sub-threads are hidden from the main feed by default. The
    // History tab passes showCompleted to surface them instead. EXCEPTION: the
    // sub-thread OPEN in the modal is ALWAYS kept — a status change (e.g. to
    // COMPLETED) must never unmount it out from under the open modal.
    .filter(st => st.key === openThreadId || showCompleted || subjectOf(st.msgs).status !== 'COMPLETED')
    .sort((a, b) => {
      const la = Math.max(...a.msgs.map(m => new Date(m.received_at).getTime()));
      const lb = Math.max(...b.msgs.map(m => new Date(m.received_at).getTime()));
      return lb - la;
    });

  // Nothing active for this contact — hide the whole card.
  if (subThreads.length === 0) return null;

  // The sub-thread currently OPEN in the modal, if it belongs to THIS card. In an
  // employee conversation the open thread is rendered in a fixed, group-independent
  // STABLE SLOT (below) and EXCLUDED from the customer groups — so a background
  // refresh that reassigns its target customer (which moves it into a different,
  // by-default-collapsed customer group) or flips its status can't unmount it and
  // close the modal out from under the rep. The modal is a document.body portal, so
  // only the SubThread's continuous mount matters; pinning its tree position here
  // guarantees it. (Michael: the modal must stay open at all times — tab and
  // customer sub-category placements happen in the background.)
  const openSt = openThreadId != null ? (subThreads.find(st => st.key === openThreadId) || null) : null;

  // Employee-conversation detection (#7 three-level hierarchy). This whole card
  // is an EMPLOYEE conversation when ANY message carries is_employee === true
  // (the backend now keeps the flag true for the entire employee thread), with a
  // legacy fallback to the "[EMPLOYEE]" customer_name prefix for rows that predate
  // the flag. A NORMAL customer conversation leaves this false and renders the
  // sub-threads directly — byte-identical to today, no middle level.
  const isEmployeeConversation =
    messages.some(m => m.is_employee === true) ||
    messages.some(m => /^\s*\[employee\]/i.test(m.customer_name || ''));

  // EMPLOYEE conversation only: group the (already filtered/sorted) sub-threads
  // by their ROOT (subject) message's customer_id — the resolved target customer.
  // Derived from props on EVERY render (no memo), so when a rep changes a
  // sub-thread's target via the dropdown and the feed refreshes, the sub-thread's
  // root customer_id changes and it moves to the new customer's group on the next
  // render automatically. Sub-threads whose root customer_id is null land in the
  // "Unassigned / pending customer" group, rendered FIRST. Resolved groups follow,
  // sorted stably by customer_name.
  type CustomerGroup = { customerId: number | null; customerName: string; subThreads: typeof subThreads };
  const buildCustomerGroups = (): CustomerGroup[] => {
    const unassigned: CustomerGroup = { customerId: null, customerName: 'Unassigned / pending customer', subThreads: [] };
    const byCustomer = new Map<number, CustomerGroup>();
    for (const st of subThreads) {
      // The open sub-thread renders in the stable slot, not inside a group — so a
      // background customer reassignment can't drop it into a collapsed group and
      // unmount the modal.
      if (openSt && st.key === openSt.key) continue;
      const root = subjectOf(st.msgs);
      const cid = root.customer_id ?? null;
      if (cid == null) {
        unassigned.subThreads.push(st);
        continue;
      }
      let grp = byCustomer.get(cid);
      if (!grp) {
        grp = { customerId: cid, customerName: root.customer_name || ('Customer #' + cid), subThreads: [] };
        byCustomer.set(cid, grp);
      }
      grp.subThreads.push(st);
    }
    const resolved = Array.from(byCustomer.values()).sort((a, b) =>
      a.customerName.localeCompare(b.customerName)
    );
    // Unassigned group renders first, and only when it actually has sub-threads.
    return (unassigned.subThreads.length > 0 ? [unassigned] : []).concat(resolved);
  };
  const customerGroups = isEmployeeConversation ? buildCustomerGroups() : [];

  // Move-message targets: every sub-thread in THIS conversation, labelled by its
  // THREAD NUMBER (+ customer for context), so a rep can move a mis-threaded
  // inbound to the right one. Built from the full threadMap (so a completed/hidden
  // thread is still a valid destination).
  const allThreads = Array.from(threadMap.entries()).map(([key, msgs]) => {
    const s = subjectOf(msgs);
    const who = s.customer_name || s.sender_id || '';
    return { key, label: who ? `#${key} · ${who}` : `Thread #${key}` };
  });
  const handleReassign = (messageId: number, threadId: number) =>
    reassignMessageThread(messageId, threadId).then(() => onAfterSend()).catch(() => onAfterSend());
  const handleSplitToNewThread = (messageId: number) =>
    splitMessageToNewThread(messageId).then(() => onAfterSend()).catch(() => onAfterSend());

  // A single sub-thread card — extracted so both the normal (flat) path and the
  // employee (grouped) path render it identically.
  const renderSubThread = (st: { key: number; msgs: Message[] }) => (
    <SubThread
      key={st.key}
      messages={st.msgs}
      onStatusChange={onStatusChange}
      onAfterSend={onAfterSend}
      otherThreads={allThreads.filter(t => t.key !== st.key)}
      onReassign={handleReassign}
      onSplitToNewThread={handleSplitToNewThread}
      // Open when this thread is the lifted open id; toggle opens it (via the
      // shared setter) or closes the modal (null). The modal is keyed on this id
      // in MessageFeed, so a background poll/re-sort of the list rows underneath
      // never unmounts or moves it.
      isOpen={openThreadId === st.key}
      onToggle={() => onOpenThread(openThreadId === st.key ? null : st.key)}
      modalView={modalView}
      onSetModalView={setModalView}
    />
  );

  const latest = [...messages].sort((a, b) => msgTime(b) - msgTime(a))[0];
  // Most recent INBOUND ("ingested") message across this contact's threads —
  // surfaced on the collapsed header so a rep can see at a glance when the
  // customer last reached out, without expanding. Falls back to the newest
  // message overall on the (unexpected) chance there's no inbound row.
  const latestInbound = messages
    .filter(m => m.direction !== 'outbound')
    .sort((a, b) => msgTime(b) - msgTime(a))[0];
  const latestIngestedAt = (latestInbound || latest).received_at;
  const named =
    messages.find(m => m.direction !== 'outbound' && m.customer_name) ||
    messages.find(m => m.customer_name);
  const customerName = named?.customer_name || latest.sender_id;
  const customerId = messages.find(m => m.customer_id)?.customer_id ?? null;
  // For an EMPLOYEE conversation the TOP-LEVEL identity is the REP, not a customer —
  // one employee texts on behalf of MANY customers, and the employee's number can
  // ALSO sit on a customer account, which would otherwise surface that customer's
  // name at the top. Prefer the durable rep name (employee_sender_name); else strip
  // the legacy "[EMPLOYEE]" prefix off a message that actually carries it (NOT a
  // resolved customer name); else the phone. Customer names stay on the per-customer
  // groups below.
  const employeeRepName = isEmployeeConversation
    ? (messages.map(m => m.employee_sender_name).find(n => !!(n && n.trim()))
       || (() => {
            const tagged = messages.find(m => /^\s*\[employee\]/i.test(m.customer_name || ''));
            const stripped = tagged ? (tagged.customer_name || '').replace(/^\s*\[employee\]\s*/i, '').trim() : '';
            return stripped || null;
          })()
       || null)
    : null;
  const headerName = isEmployeeConversation ? (employeeRepName || latest.sender_id) : customerName;
  // Conversation-level review flag: true when any sub-thread/message in the card
  // is flagged for human review.
  const convoNeedsReview = subThreads.some(st => subjectOf(st.msgs).needs_human_review);
  // Conversation-level readiness: the green check now means "ready to approve &
  // complete", not merely "customer matched". A conversation is ready when it has
  // at least one ACTIVE (non-completed) sub-thread and EVERY active sub-thread is
  // ready_for_completion (customer resolved, no Needs-Review flag, a pending reply
  // draft, and — for ORDER/RMA — all order lines resolved), computed server-side in
  // get_messages. Mirrors how convoNeedsReview aggregates across sub-threads.
  const activeSubs = subThreads.filter(st => subjectOf(st.msgs).status !== 'COMPLETED');
  const convoReady = activeSubs.length > 0
    && activeSubs.every(st => subjectOf(st.msgs).ready_for_completion);
  // Conversation-level "awaiting reply": how many active sub-threads have an
  // unanswered inbound as their most-recent message (same rule as the SubThread
  // pill). Surfaces on the COLLAPSED conversation header so a rep can see which
  // customers are waiting on a response without expanding the card. CALL and
  // completed sub-threads are excluded.
  const threadAwaitingReply = (msgs: Message[]): boolean => {
    const s = subjectOf(msgs);
    if (s.status === 'COMPLETED' || s.channel === 'CALL') return false;
    const chron = [...msgs].sort((a, b) => msgTime(a) - msgTime(b) || (a.id - b.id));
    const last = chron[chron.length - 1];
    return !!last && last.direction !== 'outbound';
  };
  const awaitingCount = subThreads.filter(st => threadAwaitingReply(st.msgs)).length;

  return (
    <div ref={rootRef} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden max-w-full">
      <div
        className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1">
            <ChannelBadge channel={latest.channel} isVoicemail={isVoicemailMessage(latest)} className="flex-shrink-0" />
            <span className="font-semibold text-gray-900 truncate">{headerName}</span>
            {isEmployeeConversation && (
              <span
                className="flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800"
                title="Employee/rep texting on behalf of customers"
              >
                Employee
              </span>
            )}
            {convoNeedsReview && (
              <span className="px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-amber-100 text-amber-800 flex-shrink-0 whitespace-nowrap">
                Needs Review
              </span>
            )}
            {/* "Awaiting reply" — one or more active sub-threads whose latest
                message is an unanswered customer message. Shows the count when >1
                so a rep sees at a glance this conversation is waiting on them. */}
            {awaitingCount > 0 && (
              <span
                title={awaitingCount === 1
                  ? 'A thread here is waiting on a reply'
                  : `${awaitingCount} threads here are waiting on a reply`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-blue-100 text-blue-800 flex-shrink-0 whitespace-nowrap"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                {awaitingCount === 1 ? 'Awaiting reply' : `${awaitingCount} awaiting reply`}
              </span>
            )}
            {/* The green check means the conversation is READY to approve & complete
                (customer resolved, reply drafted, nothing flagged, order lines all
                resolved) — see convoReady. Readiness is per-customer on an employee
                thread, not a single top-level state, so show it only for real
                customer conversations. */}
            {!isEmployeeConversation && convoReady && (
              <span
                title="Ready to approve & complete — customer resolved, reply drafted, no items left to resolve"
                className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            <span
              className="text-xs text-gray-400 whitespace-nowrap hidden sm:inline"
              title="Most recent message received"
            >
              {fmtTime(latestIngestedAt)}
            </span>
            <span className="text-xs text-gray-300 hidden sm:inline" aria-hidden>·</span>
            <span className="text-xs text-gray-500">
              {subThreads.length} {subThreads.length === 1 ? 'thread' : 'threads'}
            </span>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 px-3 py-3 space-y-2">
          {/* Non-employee conversation: the sender IS the customer, so the customer
              info + recent orders belong at the top. Employee conversations show NO
              customer panel here — it moves down into each customer group below. */}
          {!isEmployeeConversation && (
            <CustomerPanel customerId={customerId} customerName={customerName} senderId={latest.sender_id} onCreated={onAfterSend} />
          )}
          {/* STABLE SLOT — the open sub-thread ALWAYS renders here, at a FIXED tree
              position that is invariant across isEmployeeConversation flips, customer
              regrouping, and background reshuffles. SubThread is keyed by its thread
              id, so holding its POSITION fixed is what keeps it (and its ThreadModal
              portal + the order-preview editor's local edit state) MOUNTED when a
              thread's customer is reassigned. Previously this slot lived INSIDE the
              employee-only branch below, so reassigning a customer — which can flip
              isEmployeeConversation or move the thread between customer groups —
              switched the ternary branch and REMOUNTED the whole subtree, wiping
              unsaved preview edits and resetting the modal (thread #5015). It is
              excluded from BOTH lists below so it never renders twice. */}
          {openSt && renderSubThread(openSt)}
          {isEmployeeConversation ? (
            <>
            {
            // EMPLOYEE conversation: three-level hierarchy. A middle customer-group
            // header (the customer_name) sits above that customer's sub-threads.
            // Unassigned/pending group renders first (see buildCustomerGroups).
            customerGroups.map(grp => {
              const gkey = grp.customerId == null ? 'unassigned' : 'cust-' + grp.customerId;
              const gOpen = openGroups.has(gkey);
              const grpNeedsReview = grp.subThreads.some(st => subjectOf(st.msgs).needs_human_review);
              const grpAwaiting = grp.subThreads.filter(st => threadAwaitingReply(st.msgs)).length;
              return (
              <div key={gkey} className="space-y-2">
                {/* Collapsible customer-group header: click to reveal that customer's
                    info + threads. Collapsed by default so the employee thread is easy
                    to scan; a Needs-Review flag shows which group to open. */}
                <div
                  className="flex flex-wrap items-center gap-2 px-1 pt-1 cursor-pointer select-none hover:bg-gray-100 rounded"
                  onClick={() => toggleGroup(gkey)}
                >
                  <svg
                    className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${gOpen ? 'rotate-90' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z" />
                  </svg>
                  <span
                    className={`text-sm font-semibold truncate ${
                      grp.customerId == null ? 'text-amber-700' : 'text-gray-700'
                    }`}
                  >
                    {grp.customerName}
                  </span>
                  {grpNeedsReview && (
                    <span className="flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                      Needs Review
                    </span>
                  )}
                  {grpAwaiting > 0 && (
                    <span
                      title={grpAwaiting === 1
                        ? 'A thread here is waiting on a reply'
                        : `${grpAwaiting} threads here are waiting on a reply`}
                      className="inline-flex items-center gap-1 flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                      {grpAwaiting === 1 ? 'Awaiting reply' : `${grpAwaiting} awaiting reply`}
                    </span>
                  )}
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {grp.subThreads.length} {grp.subThreads.length === 1 ? 'thread' : 'threads'}
                  </span>
                </div>
                {gOpen && (
                  <div className="pl-2 sm:pl-3 border-l-2 border-gray-200 space-y-2">
                    {/* This customer's NetSuite info + recent orders — scoped to the
                        group's resolved customer. Skipped for the unassigned group. */}
                    {grp.customerId != null && (
                      <CustomerPanel customerId={grp.customerId} customerName={grp.customerName} senderId={latest.sender_id} onCreated={onAfterSend} />
                    )}
                    {grp.subThreads.map(st => renderSubThread(st))}
                  </div>
                )}
              </div>
              );
            })
            }
            </>
          ) : (
            // Non-employee conversation. The OPEN thread is already rendered in the
            // stable slot above, so exclude it here to avoid a double mount (and so
            // it keeps that fixed position rather than its sorted index among siblings).
            subThreads.filter(st => !openSt || st.key !== openSt.key).map(st => renderSubThread(st))
          )}
        </div>
      )}
    </div>
  );
};

// Memoized: with stable callback props from MessageFeed and the feed's
// identity-skip on unchanged data, a card whose `messages` array reference hasn't
// changed will NOT re-render when the feed re-renders for unrelated reasons —
// preventing the open thread from reflowing/flickering during background reloads.
export default React.memo(ConversationCard);
